import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { styles, fmtRole, fmtDateTime } from '../components/UI';
import ErrorBoundary from '../components/ErrorBoundary';
import UploadStatusBadge from '../components/UploadStatusBadge';
import DropZone from '../components/DropZone';
import { HascRegistrationAdmin } from '../components/HascRegistration';

// Admin tab structure -- grouped into three labeled sections so the
// 11 tabs aren't a single confusing wide row. Sales = revenue/AM tools,
// Branch = branch-level setup + budgets, Admin = system administration.
const ADMIN_GROUPS = [
  { label: 'Sales', tabs: [
    { v: 'targets',     label: 'Performance Targets' },
    { v: 'sales-rev',   label: 'Sales Revenue' },
    { v: 'commissions', label: 'Commissions' }
  ]},
  { label: 'Branch', tabs: [
    { v: 'branches',    label: 'Branches' },
    { v: 'budgets',     label: 'Branch Budgets' }
  ]},
  { label: 'Admin', tabs: [
    { v: 'users',       label: 'Users' },
    { v: 'import',      label: 'Import' },
    { v: 'mail',        label: 'Mail' },
    { v: 'cleanup',     label: 'Cleanup' },
    { v: 'backup',      label: 'Backup' },
    { v: 'settings',    label: 'Settings' }
  ]}
];

export default function Admin() {
  // Sub-tab lives in the URL (?tab=...) so a hard refresh, bookmark, or
  // shared link lands on the same tab. Falls back to "users" when missing
  // or unrecognized. Built from ADMIN_GROUPS so adding a new tab to the
  // group config automatically extends the whitelist.
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ADMIN_GROUPS.flatMap(g => g.tabs.map(t => t.v));
  const tab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'users';
  const setTab = (v) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    next.set('tab', v);
    return next;
  });
  return (
    <>
      <h1 style={styles.h1}>Admin</h1>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {ADMIN_GROUPS.map(group => (
          <div key={group.label} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 10, fontWeight: 700, color: '#9CA3AF', letterSpacing: 1.5,
              textTransform: 'uppercase', width: 64, flexShrink: 0
            }}>{group.label}</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {group.tabs.map(t => (
                <button
                  key={t.v}
                  style={tab === t.v ? styles.tabBtnActive : styles.tabBtn}
                  onClick={() => setTab(t.v)}
                >{t.label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <ErrorBoundary key={tab}>
        {tab === 'users' && <Users />}
        {tab === 'branches' && <Branches />}
        {tab === 'import' && <ImportTab />}
        {tab === 'targets' && <PerformanceTargetsTab />}
        {tab === 'sales-rev' && <SalesRevenueTab />}
        {tab === 'budgets' && <BranchBudgetsTab />}
        {tab === 'commissions' && <CommissionsTab />}
        {tab === 'mail' && <MailTab />}
        {tab === 'cleanup' && <CleanupTab />}
        {tab === 'backup' && <BackupTab />}
        {tab === 'settings' && <SettingsTab />}
      </ErrorBoundary>
    </>
  );
}

// ---- Users ----------------------------------------------------------

// Temp password for a new user or an import batch. Generated fresh on every
// use (hardening 2026-09-05): the old shared literal 'ChangeMe2026!' lived in
// this public bundle, so every account created before its first login shared
// one guessable password. Every new user is still created with
// must_change_password=true, so whichever value is used only lasts until the
// user's first login.
const genTempPassword = () => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const bytes = new Uint8Array(12);
  window.crypto.getRandomValues(bytes);
  return 'Tmp-' + Array.from(bytes, b => alphabet[b % alphabet.length]).join('') + '!';
};
const blankUser = () => ({
  username: '', password: genTempPassword(), full_name: '', email: '', phone: '',
  role: 'rep', branch_id: null, supervisor_id: null, is_active: true,
  sales_id: '', birthday: '', hire_date: '',
  // Commission rate defaults match the standard plan (3% rentals / 2% labor /
  // 1% consumables). Admin can override on a per-user basis.
  // show_commission_rates is OFF by default -- admin opts the user in
  // before the rates / earned $ appear on their dashboard widget.
  commission_rentals_pct: 3, commission_labor_pct: 2, commission_consumables_pct: 1,
  show_commission_rates: false,
  // show_on_by_rep: drives visibility on the Dashboard "By Account Manager"
  // table. Default true; the server flips it to false on POST for HQ roles
  // (admin / leadership / ops). Admin can toggle freely on edit.
  show_on_by_rep: true,
  // route_to_house_account: when true, every Rentalman row tagged to this
  // user gets re-routed to House Account on import. Use case: an active
  // user (admin/leadership login required) whose old AM sales_id is still
  // in the Rentalman feed but whose revenue shouldn't credit them.
  route_to_house_account: false,
  // hide_sensitive_info: walls the user off employee SSN / DL# / DOB
  // (badging profiles + AI extraction). Defaults ON for every role except
  // admin / leadership; picking a role resets it to that role's default.
  hide_sensitive_info: true,
  // Reps this user is the supervisor of. Only meaningful when role is
  // supervisor / leadership / admin. When edited, the server bulk-updates
  // each listed rep's supervisor_id to point at this user.
  direct_report_ids: []
});

// Display "May 15" / "Jan 3" from a YYYY-MM-DD birthday. Year is dropped
// because we only use month + day for the birthday-wishes cron.
const fmtBirthday = (b) => {
  if (!b) return '';
  const s = String(b).slice(0, 10);
  const d = new Date(s + 'T00:00:00');
  if (isNaN(d)) return s;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function Users() {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [welcomeTarget, setWelcomeTarget] = useState(null); // user being emailed
  const [error, setError] = useState('');
  const [justSaved, setJustSaved] = useState('');

  const reload = () => {
    api.listUsers().then(rs => {
      setRows(rs);
      setError('');
      // Eligible supervisors = anyone above a rep. Including leadership + admin
      // means reps can report directly to leadership at org bootstrap (no
      // supervisors yet) or in small branches without a dedicated supervisor.
      setSupervisors(rs.filter(r => ['supervisor','leadership','admin'].includes(r.role) && r.is_active));
    }).catch(e => setError(e.message));
  };

  const remove = async (u) => {
    if (!confirm(`Delete user "${u.full_name}"? This is permanent. (If they have call log or project history, the system will refuse and tell you to deactivate instead.)`)) return;
    setError('');
    try {
      await api.deleteUser(u.id);
      reload();
    } catch (err) { setError(err.message); }
  };

  useEffect(() => {
    reload();
    api.listBranches().then(setBranches).catch(() => {});
  }, []);

  const scrollToForm = () => setTimeout(() =>
    document.getElementById('user-edit-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
  50);
  const startCreate = () => { setJustSaved(''); setEditing(blankUser()); scrollToForm(); };
  const startEdit = (u) => {
    setJustSaved('');
    // Date columns come back as ISO timestamps; HTML date inputs need YYYY-MM-DD.
    // Commission rates come back as numeric strings from pg; coerce to Number
    // so the type=number inputs render the value cleanly.
    setEditing({
      ...u,
      password: '',
      birthday: u.birthday ? String(u.birthday).slice(0, 10) : '',
      hire_date: u.hire_date ? String(u.hire_date).slice(0, 10) : '',
      commission_rentals_pct: u.commission_rentals_pct != null ? Number(u.commission_rentals_pct) : 3,
      commission_labor_pct: u.commission_labor_pct != null ? Number(u.commission_labor_pct) : 2,
      commission_consumables_pct: u.commission_consumables_pct != null ? Number(u.commission_consumables_pct) : 1,
      show_commission_rates: !!u.show_commission_rates,
      // Treat missing column (legacy row) as TRUE -- consistent with the
      // server-side COALESCE(show_on_by_rep, true) used by the by-rep query.
      show_on_by_rep: u.show_on_by_rep !== false,
      route_to_house_account: !!u.route_to_house_account,
      hide_sensitive_info: !!u.hide_sensitive_info,
      // Pull current direct reports from the loaded users list -- anyone
      // whose supervisor_id matches this user is a direct report.
      direct_report_ids: rows.filter(r => r.supervisor_id === u.id).map(r => r.id)
    });
    scrollToForm();
  };
  const cancel = () => { setEditing(null); setJustSaved(''); };

  const save = async () => {
    setError('');
    try {
      const u = { ...editing };
      if (u.branch_id === '' || u.branch_id === undefined) u.branch_id = null;
      if (u.supervisor_id === '' || u.supervisor_id === undefined) u.supervisor_id = null;
      const wasCreate = !editing.id;
      if (editing.id) {
        if (!u.password) delete u.password;
        await api.updateUser(editing.id, u);
      } else {
        await api.createUser(u);
      }
      reload();
      if (wasCreate) {
        // Stay on the form, blank, so admin can keep adding users.
        setJustSaved(`Created ${u.full_name}`);
        setEditing(blankUser());
      } else {
        setEditing(null);
      }
    } catch (err) { setError(err.message); }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ ...styles.h2, margin: 0 }}>Users</h2>
        <button style={styles.btn} onClick={startCreate}>+ New User</button>
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}

      {editing && (
        <div id="user-edit-form" style={styles.card}>
          <h2 style={styles.h2}>{editing.id ? 'Edit User' : 'New User'}</h2>
          {justSaved && (
            <div style={{ background: '#D1FAE5', color: '#065F46', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
              {justSaved}. Form cleared — add another or hit Cancel when done.
            </div>
          )}
          <div style={styles.formGrid}>
            <Field label={editing.id ? 'Username * (typo-fix only — user must log in with the new value)' : 'Username *'}>
              <input style={styles.input} value={editing.username} onChange={e => {
                const v = e.target.value;
                setEditing(prev => ({
                  ...prev,
                  username: v,
                  // Auto-fill email from username if user hasn't typed one yet.
                  email: prev.id ? prev.email : (prev.email && !prev.email.endsWith('@selectsafetyusa.com')) ? prev.email
                       : v ? `${v.toLowerCase().trim()}@selectsafetyusa.com` : ''
                }));
              }} />
            </Field>
            <Field label="Full Name *">
              <input style={styles.input} value={editing.full_name} onChange={e => setEditing({ ...editing, full_name: e.target.value })} />
            </Field>
            <Field label="Email">
              <input type="email" style={styles.input} placeholder="user@selectsafetyusa.com" value={editing.email || ''} onChange={e => setEditing({ ...editing, email: e.target.value })} />
            </Field>
            <Field label="Phone">
              <input type="tel" style={styles.input} placeholder="(361) 555-1234" value={editing.phone || ''} onChange={e => setEditing({ ...editing, phone: e.target.value })} />
            </Field>
            <Field label={editing.id ? 'New Password (leave blank to keep)' : 'Temp Password *'}>
              {/* Create mode shows the generated temp password in the clear — the
                  admin has to hand it to the new user (2026-09-07: masked meant an
                  account nobody could log into). Edit mode stays masked. */}
              <input type={editing.id ? 'password' : 'text'} style={styles.input} value={editing.password || ''} onChange={e => setEditing({ ...editing, password: e.target.value })} />
              {!editing.id && (
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                  User is forced to change this on first login.
                </div>
              )}
            </Field>
            <Field label="Role *">
              <select style={styles.input} value={editing.role} onChange={e => setEditing({ ...editing, role: e.target.value, hide_sensitive_info: !['admin', 'leadership'].includes(e.target.value) })}>
                <option value="rep">Account Manager</option>
                <option value="supervisor">Supervisor</option>
                <option value="leadership">Leadership</option>
                <option value="admin">Admin</option>
                <option value="ops">Ops</option>
              </select>
            </Field>
            <Field label="Branch">
              <select style={styles.input} value={editing.branch_id || ''} onChange={e => setEditing({ ...editing, branch_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">—</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="Supervisor">
              <select style={styles.input} value={editing.supervisor_id || ''} onChange={e => setEditing({ ...editing, supervisor_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">—</option>
                {supervisors.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.full_name} ({fmtRole(s.role)})
                  </option>
                ))}
              </select>
              {supervisors.length === 0 && (
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                  No eligible supervisors yet. Create a supervisor (or leadership/admin) user first.
                </div>
              )}
            </Field>
            <Field label="Sales ID (Rentalman)">
              <input style={styles.input} placeholder="e.g. RG01" value={editing.sales_id || ''} onChange={e => setEditing({ ...editing, sales_id: e.target.value })} />
            </Field>
            <Field label="Birthday">
              <input
                type="date"
                style={styles.input}
                value={editing.birthday || ''}
                onChange={e => setEditing({ ...editing, birthday: e.target.value })}
              />
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                Year ignored — only month + day used for the auto-birthday email.
              </div>
            </Field>
            <Field label="Hire Date">
              <input
                type="date"
                style={styles.input}
                value={editing.hire_date || ''}
                onChange={e => setEditing({ ...editing, hire_date: e.target.value })}
              />
            </Field>
            {/* Commission rate overrides. Defaults: 3% / 2% / 1%. Admin
                edits these only when a user has a non-standard plan. */}
            <Field label="Commission % · Rentals">
              <input
                type="number" step="0.01" min="0"
                style={styles.input}
                value={editing.commission_rentals_pct ?? ''}
                onChange={e => setEditing({ ...editing, commission_rentals_pct: e.target.value })}
              />
            </Field>
            <Field label="Commission % · Labor">
              <input
                type="number" step="0.01" min="0"
                style={styles.input}
                value={editing.commission_labor_pct ?? ''}
                onChange={e => setEditing({ ...editing, commission_labor_pct: e.target.value })}
              />
            </Field>
            <Field label="Commission % · Consumables">
              <input
                type="number" step="0.01" min="0"
                style={styles.input}
                value={editing.commission_consumables_pct ?? ''}
                onChange={e => setEditing({ ...editing, commission_consumables_pct: e.target.value })}
              />
            </Field>
            <Field label="Show Commission on Dashboard">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={!!editing.show_commission_rates}
                  onChange={e => setEditing({ ...editing, show_commission_rates: e.target.checked })}
                />
                Show rates &amp; earned $ on this user's widget
              </label>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                Off by default. When ON, the rep sees their commission rates and earned $ on the Performance Targets widget.
              </div>
            </Field>
            <Field label="Show on Dashboard 'By Account Manager'">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={editing.show_on_by_rep !== false}
                  onChange={e => setEditing({ ...editing, show_on_by_rep: e.target.checked })}
                />
                Include in the AM scoreboard table
              </label>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                Account managers default ON. HQ roles (admin / leadership / ops) default OFF.
                Toggle ON for owners / VPs / supervisors who actively sell deals.
              </div>
            </Field>
            <Field label="Route Rentalman revenue to House Account">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={!!editing.route_to_house_account}
                  onChange={e => setEditing({ ...editing, route_to_house_account: e.target.checked })}
                />
                Re-route this user's Rentalman rows to House Account on import
              </label>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                When ON, every Rentalman row matched to this user (by sales_id or name)
                gets credited to House Account instead. Use for active users (admin /
                leadership) whose old AM sales_id still flows through Rentalman but
                whose revenue shouldn't pollute scoreboards or commission. Re-upload
                affected months after toggling to fix history.
              </div>
            </Field>
            <Field label="Hide sensitive info">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={!!editing.hide_sensitive_info}
                  onChange={e => setEditing({ ...editing, hide_sensitive_info: e.target.checked })}
                />
                Hide SSN, DL#, DOB from this user
              </label>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                On by default for every role except Admin &amp; Leadership (changing the
                role resets it). When ON, employee badging facts (SSN / driver's license /
                date of birth) show locked and the AI document extraction is refused.
                Untick to let this user see and edit them; tick it on an Admin or
                Leadership user to wall them off.
              </div>
            </Field>
            <Field label="Active">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8 }}>
                <input type="checkbox" checked={!!editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} />
                Active
              </label>
            </Field>
          </div>

          {/* Direct Reports -- only meaningful when this user is a
              supervisor / leadership / admin. Multi-checkbox of all
              active reps. Each ticked rep gets their supervisor_id
              set to this user on save; un-ticked reps previously
              under this supervisor get cleared back to NULL. */}
          {['supervisor', 'leadership', 'admin'].includes(editing.role) && (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #E5E7EB' }}>
              <label style={styles.label}>
                Direct Reports
                <span style={{ color: '#6B7280', fontWeight: 400, marginLeft: 8 }}>
                  · {editing.direct_report_ids?.length || 0} assigned
                </span>
              </label>
              <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>
                Tick the Account Managers this {fmtRole(editing.role)} supervises. Their data shows up on this user's dashboard,
                and a {fmtRole('supervisor')} can <strong>View As</strong> any of their direct reports.
              </div>
              <div style={{
                maxHeight: 220, overflowY: 'auto',
                border: '1px solid #E5E7EB', borderRadius: 6, padding: 8,
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 4
              }}>
                {rows
                  .filter(u => u.role === 'rep' && u.is_active && u.id !== editing.id)
                  .map(u => {
                    const checked = (editing.direct_report_ids || []).includes(u.id);
                    return (
                      <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', padding: '2px 4px' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={e => {
                            const next = new Set(editing.direct_report_ids || []);
                            if (e.target.checked) next.add(u.id); else next.delete(u.id);
                            setEditing({ ...editing, direct_report_ids: [...next] });
                          }}
                        />
                        {u.full_name}
                        {u.branch_name && <span style={{ color: '#9CA3AF', fontSize: 11 }}>· {u.branch_name}</span>}
                      </label>
                    );
                  })}
                {rows.filter(u => u.role === 'rep' && u.is_active).length === 0 && (
                  <div style={{ color: '#9CA3AF', fontSize: 12 }}>No active Account Managers to assign.</div>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={styles.btn} onClick={save}>Save</button>
            <button style={styles.btnSecondary} onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Username</th>
              <th style={styles.th}>Contact</th>
              <th style={styles.th}>Role</th>
              <th style={styles.th}>Branch</th>
              <th style={styles.th}>Supervisor</th>
              <th style={styles.th}>Sales ID</th>
              <th style={styles.th}>Active</th>
              <th style={styles.th}>Last Active</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(u => (
              <tr key={u.id}>
                <td style={styles.td}><strong>{u.full_name}</strong></td>
                <td style={styles.td}>{u.username}</td>
                <td style={styles.td}>
                  {u.email && <div style={{ fontSize: 12 }}>{u.email}</div>}
                  {u.phone && <div style={{ fontSize: 12, color: '#6B7280' }}>{u.phone}</div>}
                  {!u.email && !u.phone && <span style={{ color: '#9CA3AF' }}>—</span>}
                </td>
                <td style={styles.td}>{fmtRole(u.role)}</td>
                <td style={styles.td}>{u.branch_name || ''}</td>
                <td style={styles.td}>{u.supervisor_name || ''}</td>
                <td style={styles.td}>{u.sales_id || <span style={{ color: '#9CA3AF' }}>—</span>}</td>
                <td style={styles.td}>{u.is_active ? 'Yes' : 'No'}</td>
                <td style={styles.td}>
                  {u.last_active_at ? fmtDateTime(u.last_active_at) : <span style={{ color: '#9CA3AF' }}>Never</span>}
                  {(u.last_login_city || u.last_login_country) && (
                    <div style={{ fontSize: 11, color: '#6B7280' }} title={u.last_login_ip ? `Last login IP: ${u.last_login_ip}` : ''}>
                      from {[u.last_login_city, u.last_login_region].filter(Boolean).join(', ') || u.last_login_country}
                    </div>
                  )}
                </td>
                <td style={styles.td}>
                  <button
                    style={{ ...styles.btnSecondary, opacity: u.email ? 1 : 0.4, cursor: u.email ? 'pointer' : 'not-allowed' }}
                    disabled={!u.email}
                    title={u.email
                      ? 'Reset their password and email them login info'
                      : 'Add an email to this user first'}
                    onClick={() => { setJustSaved(''); setWelcomeTarget(u); }}
                  >📧 Welcome</button>{' '}
                  <button style={styles.btnSecondary} onClick={() => startEdit(u)}>Edit</button>{' '}
                  <button style={styles.btnDanger} onClick={() => remove(u)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {welcomeTarget && (
        <WelcomeEmailModal
          user={welcomeTarget}
          onCancel={() => setWelcomeTarget(null)}
          onSent={(msg) => { setWelcomeTarget(null); setJustSaved(msg); reload(); }}
        />
      )}
    </>
  );
}

// ---- Welcome email modal -----------------------------------------------
//
// Sends a personalized welcome email containing the CRM URL, the user's
// username, and a temp password. Server resets the user's password to that
// value and forces must_change_password=true so the rep is bounced through
// the change-password screen on first login.
function WelcomeEmailModal({ user, onCancel, onSent }) {
  const [tempPassword, setTempPassword] = useState(() => genTempPassword());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [smtpOk, setSmtpOk] = useState(null); // null = checking, true/false = result
  const crmUrl = window.location.origin;
  const firstName = (user.full_name || '').trim().split(/\s+/)[0] || 'there';

  useEffect(() => {
    api.mailStatus()
      .then(s => setSmtpOk(!!s.configured))
      .catch(() => setSmtpOk(false));
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr('');
    if (tempPassword.length < 8) {
      setErr('Temp password must be at least 8 characters.');
      return;
    }
    setBusy(true);
    try {
      const r = await api.sendWelcomeEmail(user.id, tempPassword);
      onSent(r.sent
        ? `Welcome email sent to ${user.email} — password reset.`
        : (r.reason || 'Password reset, but email did not send. Share the temp password manually.'));
    } catch (e2) {
      setErr(e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16
      }}
    >
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 8, padding: 20,
          width: '100%', maxWidth: 560, maxHeight: 'calc(100vh - 32px)', overflow: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)'
        }}
      >
        <h2 style={{ ...styles.h2, marginTop: 0 }}>📧 Send Welcome Email · {user.full_name}</h2>
        <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
          Personalized welcome from <strong>Ali M. Knox</strong> with their login info.
          Sets their password to the value below and forces a change on first login.
        </div>

        {smtpOk === false && (
          <div style={{ background: '#FEF3C7', color: '#92400E', padding: '10px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
            ⚠ SMTP isn't configured. Clicking Send will <strong>still reset the password</strong>, but no email will go out — you'll need to share the password with the user manually.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="To">
            <input style={{ ...styles.input, background: '#F3F4F6' }} value={user.email} disabled />
          </Field>
          <Field label="Username">
            <input style={{ ...styles.input, background: '#F3F4F6' }} value={user.username} disabled />
          </Field>
        </div>

        <Field label="Temp Password *">
          <input
            style={styles.input}
            value={tempPassword}
            onChange={e => setTempPassword(e.target.value)}
            autoFocus
          />
          <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
            User will be prompted to change this on first login. Generated fresh each time — copy it before you send.
          </div>
        </Field>

        <div style={{ marginTop: 12, padding: 12, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 13, color: '#374151', maxHeight: 240, overflow: 'auto' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Preview</div>
          <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: 1.5 }}>{
`Subject: Welcome to the SSS CRM, ${firstName}

Hi ${firstName},

Welcome aboard. I've set up your account in our new Sales CRM.
Here's everything you need to log in:

  URL:       ${crmUrl}
  Username:  ${user.username}
  Password:  ${tempPassword}

(One-time — you'll be asked to change it on first login.)

A few quick orientation notes:
  • Activity Log — log every customer touch.
  • Project Tracker — turnaround / opportunity bids.
  • Customers — your assigned Rentalman accounts.
  • Dashboard — greeting, quota, follow-ups, calendar.
  • Mobile — works on your phone, one-tap activity logging.

Sincerely,
Ali M. Knox
Select Safety Services, LLC.`
          }</div>
        </div>

        {err && <div style={{ ...styles.errorBox, marginTop: 12 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" style={styles.btnSecondary} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" style={styles.btn} disabled={busy}>
            {busy ? 'Sending…' : (smtpOk === false ? 'Reset Password' : 'Send Welcome Email')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---- Branches ----------------------------------------------------------

const blankBranch = () => ({ code: '', name: '', location: '', state: '', is_active: true, timezone: 'CST', manager_email: '' });

// Timezone display helpers. Offsets are relative to CST (Corp HQ).
const TZ_OPTIONS = [
  { v: 'PST', label: 'PST · -2 from CST' },
  { v: 'MST', label: 'MST · -1 from CST' },
  { v: 'CST', label: 'CST · Corp HQ' },
  { v: 'EST', label: 'EST · +1 from CST' }
];
const tzShort = (tz) => {
  const offset = { PST: -2, MST: -1, CST: 0, EST: 1 }[tz];
  if (offset == null) return tz || '—';
  if (offset === 0) return `${tz} (Corp)`;
  return `${tz} (${offset > 0 ? '+' : ''}${offset})`;
};

// State / province -> timezone map. Used to auto-fill the timezone dropdown
// when admin types a state into the branch form. Falls back to no change
// when the state isn't in the map -- admin can still pick TZ manually.
// Multi-zone states (FL, IN, KY, TN, MI, ND, SD, NE, KS, OR, ID) are
// mapped to their dominant population zone; admin can override after.
const STATE_TO_TZ = {
  // Pacific
  CA: 'PST', WA: 'PST', OR: 'PST', NV: 'PST', AK: 'PST', HI: 'PST',
  // Mountain
  AZ: 'MST', UT: 'MST', ID: 'MST', MT: 'MST', WY: 'MST', CO: 'MST', NM: 'MST',
  // Central (Corp default)
  TX: 'CST', OK: 'CST', LA: 'CST', AR: 'CST', MO: 'CST', IA: 'CST', MN: 'CST',
  WI: 'CST', IL: 'CST', MS: 'CST', AL: 'CST', KS: 'CST', NE: 'CST',
  ND: 'CST', SD: 'CST',
  // Eastern
  NY: 'EST', NJ: 'EST', PA: 'EST', OH: 'EST', MI: 'EST', GA: 'EST', FL: 'EST',
  VA: 'EST', NC: 'EST', SC: 'EST', WV: 'EST', MD: 'EST', DE: 'EST', CT: 'EST',
  MA: 'EST', RI: 'EST', VT: 'EST', NH: 'EST', ME: 'EST',
  KY: 'EST', TN: 'EST', IN: 'EST', DC: 'EST',
  // Canadian provinces (some Rentalman customers are in ON)
  ON: 'EST', QC: 'EST', NB: 'EST', NS: 'EST', PE: 'EST', NL: 'EST',
  BC: 'PST', AB: 'MST', MB: 'CST', SK: 'CST'
};

function Branches() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [justSaved, setJustSaved] = useState('');

  // NOTE: braces matter -- without them the arrow's implicit return is the
  // Promise, and React tries to call that Promise as a useEffect cleanup
  // function on unmount, throwing "c is not a function" globally.
  const reload = () => { api.listAllBranches().then(setRows).catch(e => setError(e.message)); };
  useEffect(() => { reload(); }, []);

  const scrollToForm = () => setTimeout(() =>
    document.getElementById('branch-edit-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
  50);
  const startCreate = () => { setJustSaved(''); setEditing(blankBranch()); scrollToForm(); };
  const startEdit = (b) => { setJustSaved(''); setEditing({ ...b }); scrollToForm(); };
  const cancel = () => { setEditing(null); setJustSaved(''); };

  const save = async () => {
    setError('');
    try {
      const wasCreate = !editing.id;
      const name = editing.name;
      if (editing.id) await api.updateBranch(editing.id, editing);
      else await api.createBranch(editing);
      reload();
      if (wasCreate) {
        setJustSaved(`Created ${name}`);
        setEditing(blankBranch());
      } else {
        setEditing(null);
      }
    } catch (err) { setError(err.message); }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ ...styles.h2, margin: 0 }}>Branches</h2>
        <button style={styles.btn} onClick={startCreate}>+ New Branch</button>
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}

      {editing && (
        <div id="branch-edit-form" style={styles.card}>
          <h2 style={styles.h2}>{editing.id ? 'Edit Branch' : 'New Branch'}</h2>
          {justSaved && (
            <div style={{ background: '#D1FAE5', color: '#065F46', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
              {justSaved}. Form cleared — add another or hit Cancel when done.
            </div>
          )}
          <div style={styles.formGrid}>
            <Field label="Code *">
              <input style={styles.input} value={editing.code} onChange={e => setEditing({ ...editing, code: e.target.value })} />
            </Field>
            <Field label="Name *">
              <input style={styles.input} value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
            </Field>
            <Field label="Location">
              <input style={styles.input} value={editing.location || ''} onChange={e => setEditing({ ...editing, location: e.target.value })} />
            </Field>
            <Field label="State / Province">
              <input
                style={styles.input}
                placeholder="e.g. TX, OK, ON"
                maxLength={4}
                value={editing.state || ''}
                onChange={e => {
                  // Auto-uppercase + auto-fill timezone from the static map
                  // when the typed code is recognized. Don't clobber an
                  // explicit pick the user already made -- only auto-set
                  // when the new state's TZ differs from the current one.
                  const next = e.target.value.toUpperCase().slice(0, 4);
                  const tzFromState = STATE_TO_TZ[next];
                  setEditing(prev => ({
                    ...prev,
                    state: next,
                    timezone: tzFromState || prev.timezone || 'CST'
                  }));
                }}
              />
              {editing.state && STATE_TO_TZ[editing.state.toUpperCase()] && (
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                  Timezone auto-set to {STATE_TO_TZ[editing.state.toUpperCase()]}. Override below if needed.
                </div>
              )}
            </Field>
            <Field label="Timezone">
              <select style={styles.input} value={editing.timezone || 'CST'} onChange={e => setEditing({ ...editing, timezone: e.target.value })}>
                {TZ_OPTIONS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Branch manager email (RM quote notices)">
              <input style={styles.input} placeholder="name@selectsafetyusa.com" value={editing.manager_email || ''}
                onChange={e => setEditing({ ...editing, manager_email: e.target.value })} />
            </Field>
            <Field label="Active">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8 }}>
                <input type="checkbox" checked={!!editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} />
                Active
              </label>
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={styles.btn} onClick={save}>Save</button>
            <button style={styles.btnSecondary} onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Code</th>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Location</th>
              <th style={styles.th}>State</th>
              <th style={styles.th}>Timezone</th>
              <th style={styles.th}>Manager email</th>
              <th style={styles.th}>Active</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(b => (
              <tr key={b.id}>
                <td style={styles.td}>{b.code}</td>
                <td style={styles.td}><strong>{b.name}</strong></td>
                <td style={styles.td}>{b.location}</td>
                <td style={styles.td}>{b.state || <span style={{ color: '#9CA3AF' }}>—</span>}</td>
                <td style={styles.td}>{tzShort(b.timezone)}</td>
                <td style={styles.td}>{b.manager_email || <span style={{ color: '#9CA3AF' }}>—</span>}</td>
                <td style={styles.td}>{b.is_active ? 'Yes' : 'No'}</td>
                <td style={styles.td}><button style={styles.btnSecondary} onClick={() => startEdit(b)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label style={styles.label}>{label}</label>
    {children}
  </div>
);

// Compact pill-style toggle. Shows ON/OFF state and click swaps it.
const ToggleRow = ({ on, onChange, disabled }) => (
  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
    <span style={{ fontSize: 12, fontWeight: 700, color: on ? '#10B981' : '#9CA3AF', letterSpacing: 0.5 }}>
      {on ? 'ON' : 'OFF'}
    </span>
    <span
      onClick={() => !disabled && onChange()}
      style={{
        position: 'relative', display: 'inline-block', width: 40, height: 22,
        background: on ? '#10B981' : '#D1D5DB', borderRadius: 999, transition: 'background 0.2s'
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: on ? 20 : 2,
        width: 18, height: 18, background: '#fff', borderRadius: '50%',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
      }} />
    </span>
  </label>
);

// ---- Import tab ----------------------------------------------------------

function ImportTab() {
  const [reps, setReps] = useState([]);
  const [repId, setRepId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [tempPassword, setTempPassword] = useState(() => genTempPassword());
  const [projectDefaultStatus, setProjectDefaultStatus] = useState('');

  // Staged files per import type. Picking a file in the file input only
  // sets state -- the upload doesn't fire until admin clicks the explicit
  // Upload button below it. Lets them confirm the filename, change the
  // temp password / default status / target rep before committing.
  const [stagedUsers, setStagedUsers] = useState(null);
  const [stagedProjects, setStagedProjects] = useState(null);
  const [stagedCallLog, setStagedCallLog] = useState(null);
  const [stagedCustomers, setStagedCustomers] = useState(null);
  const [stagedReqs, setStagedReqs] = useState(null);

  useEffect(() => {
    // Don't auto-pick a rep -- forcing admin to choose every time the page
    // loads (and after each call-log upload) prevents the "uploaded to the
    // wrong rep" mistake.
    api.listReps().then(setReps).catch(() => {});
  }, []);

  const runImportUsers = async () => {
    if (!stagedUsers) return;
    if (!tempPassword || tempPassword.length < 8) { setError('Temp password must be at least 8 characters'); return; }
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await api.importUsers(stagedUsers, tempPassword);
      setResult({ type: 'users', ...r });
      setStagedUsers(null);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const runImportProjects = async () => {
    if (!stagedProjects) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await api.importProjects(stagedProjects, projectDefaultStatus || null);
      setResult({ type: 'projects', ...r });
      setStagedProjects(null);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const runImportCallLog = async () => {
    if (!stagedCallLog) return;
    if (!repId) { setError('Pick an account manager first'); return; }
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await api.importCallLog(stagedCallLog, repId);
      setResult({ type: 'call-log', ...r });
      setStagedCallLog(null);
      // Reset rep picker too -- next upload must explicitly pick again so
      // we never accidentally send to the previous rep on rapid uploads.
      setRepId('');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  // Rentalman Customer Master upload. Re-uploading the same export will
  // upsert by Account # so admin can refresh the list any time.
  const runImportCustomers = async () => {
    if (!stagedCustomers) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await api.importCustomers(stagedCustomers, false);
      setResult({ type: 'customers', ...r });
      setStagedCustomers(null);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  // Customer sites + requirements mass import. Dry-run preview first, then
  // a confirm() with the counts (and any unmatched-customer errors) before
  // anything is written; the commit itself is one transaction server-side.
  const runImportReqs = async () => {
    if (!stagedReqs) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const p = await api.importCustomerRequirements(stagedReqs, { dry: true });
      const errLines = (p.errors || []).slice(0, 10).map(e => `Row ${e.row}: ${e.error}`).join('\n');
      const msg = `Ready to import:\n\n${p.rows} requirement row(s) across ${p.customers} customer(s)\n` +
        `${p.customer_level_rows} customer-level · ${p.site_rows} site-level (${p.new_sites} new site(s) will be created)` +
        (p.errors?.length ? `\n\n⚠ ${p.errors.length} row(s) will be SKIPPED:\n${errLines}${p.errors.length > 10 ? '\n…and more' : ''}` : '') +
        `\n\nEach row REPLACES that customer/site's requirement values. Continue?`;
      if (!confirm(msg)) { setBusy(false); return; }
      const r = await api.importCustomerRequirements(stagedReqs);
      setResult({ type: 'requirements', ...r });
      setStagedReqs(null);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  // Reusable bit: filename chip + Upload button for a staged file.
  const StageRow = ({ file, onClear, onUpload, disabled }) => {
    if (!file) return null;
    return (
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8, padding: '8px 12px', background: '#F3F4F6', borderRadius: 6 }}>
        <span style={{ fontSize: 13 }}>📎 <strong>{file.name}</strong> <span style={{ color: '#6B7280' }}>({Math.round(file.size / 1024)} KB)</span></span>
        <button style={styles.btn} onClick={onUpload} disabled={disabled}>
          {busy ? 'Uploading…' : '↑ Upload'}
        </button>
        <button style={styles.btnSecondary} onClick={onClear} disabled={busy}>Clear</button>
      </div>
    );
  };

  return (
    <>
      <h2 style={styles.h2}>Import from Excel</h2>
      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.card}>
        <h2 style={{ ...styles.h2, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          Users (bulk create)
          <UploadStatusBadge statusKey="last_users_import" />
        </h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          Upload one row per user. Branch is matched by name or code; Supervisor is matched by full name (can reference someone in the same file). Every imported user is created with the temp password below — tell them to change it after first login.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
          <button
            style={styles.btnSecondary}
            onClick={() => api.downloadImportTemplate('users').catch(e => alert(e.message))}
          >
            ↓ Download Template
          </button>
          <span style={{ color: '#6B7280', fontSize: 12 }}>Pre-labeled headers + 3 example rows.</span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
          <label style={styles.label}>Temp password:</label>
          <input style={{ ...styles.input, width: 240 }} value={tempPassword} onChange={e => setTempPassword(e.target.value)} />
        </div>
        <DropZone onFiles={files => setStagedUsers(files[0] || null)} disabled={busy} title="Drop the users .xlsx here">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={e => { setStagedUsers(e.target.files[0] || null); e.target.value = ''; }}
            disabled={busy}
          />
        </DropZone>
        <StageRow file={stagedUsers} onClear={() => setStagedUsers(null)} onUpload={runImportUsers} disabled={busy} />
      </div>

      <div style={styles.card}>
        <h2 style={{ ...styles.h2, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          2026 Project Tracker → Projects
          <UploadStatusBadge statusKey="last_projects_import" />
        </h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          Upload the project tracker .xlsx. Branch + Account Manager are matched by name or code; unmatched rows still import (branch falls back to owner's branch). Status comes from the <code>Status</code> column in the file if present; otherwise the dropdown below applies; otherwise auto: Won if Actual &gt; 0 or % Win = 100, else Bidding. Rows missing date / duration / branch / owner are flagged for review.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
          <button
            style={styles.btnSecondary}
            onClick={() => api.downloadImportTemplate('projects').catch(e => alert(e.message))}
          >
            ↓ Download Template
          </button>
          <span style={{ color: '#6B7280', fontSize: 12 }}>Headers + example + instructions.</span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
          <label style={styles.label}>Default status (when blank in file):</label>
          <select style={{ ...styles.input, width: 200 }} value={projectDefaultStatus} onChange={e => setProjectDefaultStatus(e.target.value)}>
            <option value="">— auto-infer —</option>
            <option value="Bidding">Bidding</option>
            <option value="Active">Active</option>
            <option value="Won">Won</option>
            <option value="Lost">Lost</option>
            <option value="Complete">Complete</option>
          </select>
        </div>
        <DropZone onFiles={files => setStagedProjects(files[0] || null)} disabled={busy} title="Drop the project tracker .xlsx here">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={e => { setStagedProjects(e.target.files[0] || null); e.target.value = ''; }}
            disabled={busy}
          />
        </DropZone>
        <StageRow file={stagedProjects} onClear={() => setStagedProjects(null)} onUpload={runImportProjects} disabled={busy} />
      </div>

      <div style={styles.card}>
        <h2 style={{ ...styles.h2, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          Weekly Call Log → Call Log Entries
          <UploadStatusBadge statusKey="last_call_log_import" />
        </h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          Upload a single rep's weekly call log .xlsx. Choose the rep first — the file's "Sales Rep" header is not parsed; everything imported is assigned to the rep you pick. The "Week of" header in the file is parsed if present, else current Monday.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
          <button
            style={styles.btnSecondary}
            onClick={() => api.downloadImportTemplate('call-log').catch(e => alert(e.message))}
          >
            ↓ Download Template
          </button>
          <span style={{ color: '#6B7280', fontSize: 12 }}>Pre-labeled headers + an example row + instructions.</span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
          <label style={styles.label}>Account Manager: *</label>
          <select style={{ ...styles.input, width: 280 }} value={repId} onChange={e => setRepId(e.target.value)}>
            <option value="">— Pick Account Manager —</option>
            {reps.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
          </select>
          {!repId && <span style={{ fontSize: 12, color: '#92400E' }}>Pick a rep before uploading</span>}
        </div>
        <DropZone onFiles={files => setStagedCallLog(files[0] || null)} disabled={busy || !repId} title="Drop the call log .xlsx here (pick the rep first)">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={e => { setStagedCallLog(e.target.files[0] || null); e.target.value = ''; }}
            disabled={busy || !repId}
          />
        </DropZone>
        <StageRow file={stagedCallLog} onClear={() => setStagedCallLog(null)} onUpload={runImportCallLog} disabled={busy || !repId} />
      </div>

      <div style={styles.card}>
        <h2 style={{ ...styles.h2, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          Rentalman Customer Master → Customers
          <UploadStatusBadge statusKey="last_customers_import" />
        </h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          Upload the Customer Master export from Rentalman (.csv or .xlsx).
          Rows upsert by <code>Account #</code> — re-uploading the latest export refreshes the list.
          The importer pulls Location #, Sales Rep, Status (A/B/D/S), Name, Address, State, Zip, Phone, SIC Code, and Email.
          Customers map to Account Managers by <strong>Sales ID</strong>; set each user's Sales ID under <em>Admin → Users</em> first so dead-customer + per-rep filters work.
        </p>
        <DropZone onFiles={files => setStagedCustomers(files[0] || null)} disabled={busy} title="Drop the Rentalman customer master export here">
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={e => { setStagedCustomers(e.target.files[0] || null); e.target.value = ''; }}
            disabled={busy}
          />
        </DropZone>
        <StageRow file={stagedCustomers} onClear={() => setStagedCustomers(null)} onUpload={runImportCustomers} disabled={busy} />
      </div>

      <div style={styles.card}>
        <h2 style={{ ...styles.h2, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          Customer Sites &amp; Requirements (mass import)
        </h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          One row per requirements record: blank <code>Site Name</code> = the customer-level record; a Site Name
          <strong> creates the site under the customer</strong> (with address) plus its own override. Covers DISA,
          Training, Safety Council (classes + codes), Per Diem qualification, Per Diem Bill Rate, and Badging email /
          required documents. Customers must already exist (import the Rentalman master above first) — unmatched rows
          are skipped and reported. Each row <strong>replaces</strong> that customer/site's saved requirement values.
          You'll see a preview with counts before anything is written.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
          <button style={styles.btnSecondary} onClick={() => api.downloadReqsTemplate().catch(err => setError(err.message))}>
            ↓ Download Template
          </button>
          <span style={{ color: '#6B7280', fontSize: 12 }}>Headers + 2 example rows (customer-level + site-level) + instructions sheet.</span>
        </div>
        <DropZone onFiles={files => setStagedReqs(files[0] || null)} disabled={busy} title="Drop the sites & requirements file here">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={e => { setStagedReqs(e.target.files[0] || null); e.target.value = ''; }}
            disabled={busy}
          />
        </DropZone>
        <StageRow file={stagedReqs} onClear={() => setStagedReqs(null)} onUpload={runImportReqs} disabled={busy} />
      </div>

      {result && result.type === 'requirements' && (
        <div style={styles.card}>
          <h2 style={styles.h2}>Import Result</h2>
          <p>
            Wrote <strong>{result.requirements_written}</strong> requirement record{result.requirements_written === 1 ? '' : 's'} across{' '}
            <strong>{result.customers}</strong> customer{result.customers === 1 ? '' : 's'}
            {' '}({result.customer_level_rows} customer-level · {result.site_rows} site-level)
            {result.sites_created > 0 && <> · created <strong>{result.sites_created}</strong> new site{result.sites_created === 1 ? '' : 's'}</>}.
          </p>
          {result.errors?.length > 0 && (
            <div style={{ background: '#FEF3C7', color: '#92400E', padding: 10, borderRadius: 6, marginTop: 8, fontSize: 13 }}>
              <strong>{result.errors.length} row(s) skipped:</strong>
              <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>
                {result.errors.slice(0, 20).map(e => `Row ${e.row}: ${e.error}`).join('\n')}{result.errors.length > 20 ? '\n…and more' : ''}
              </pre>
            </div>
          )}
        </div>
      )}

      {result && result.type !== 'requirements' && (
        <div style={styles.card}>
          <h2 style={styles.h2}>Import Result</h2>
          <p>
            Inserted <strong>{result.inserted}</strong>
            {result.updated > 0 && <> · Updated <strong>{result.updated}</strong></>}
            {' '}of {result.total} rows
            {result.week_of ? ` (week of ${result.week_of})` : ''}
            {result.default_status_applied ? ` (default status: ${result.default_status_applied})` : ''}.
          </p>

          {result.unmatched_branches?.length > 0 && (
            <div style={{ background: '#FEF3C7', color: '#92400E', padding: 10, borderRadius: 6, marginTop: 8, fontSize: 13 }}>
              <strong>Unknown branches:</strong> {result.unmatched_branches.join(', ')}<br />
              These rows imported with the branch field blank. Either rename a branch's <em>code</em> in Admin → Branches to match (e.g. set Corpus Christi's code to <code>CC</code>), or fix the rows manually.
            </div>
          )}

          {result.unmatched_owners?.length > 0 && (
            <div style={{ background: '#FEF3C7', color: '#92400E', padding: 10, borderRadius: 6, marginTop: 8, fontSize: 13 }}>
              <strong>Unknown account managers:</strong> {result.unmatched_owners.join(', ')}<br />
              These rows imported with no owner. The matcher accepts first name OR full name (case-insensitive). Create the user in Admin → Users with a matching name, or fix the rows manually.
            </div>
          )}

          {result.unmatched_supervisors?.length > 0 && (
            <div style={{ background: '#FEF3C7', color: '#92400E', padding: 10, borderRadius: 6, marginTop: 8, fontSize: 13 }}>
              <strong>Unknown supervisors:</strong> {result.unmatched_supervisors.join(', ')}<br />
              These users imported with no supervisor. Edit each user in Admin → Users to assign one.
            </div>
          )}

          {result.temp_password && result.inserted > 0 && (
            <div style={{ background: '#DBEAFE', color: '#1E40AF', padding: 10, borderRadius: 6, marginTop: 8, fontSize: 13 }}>
              All imported users were created with temp password <code>{result.temp_password}</code>. Tell each user to log in and change it.
            </div>
          )}

          {result.errors && result.errors.length > 0 && (
            <>
              <p style={{ color: '#EF4444', marginTop: 12 }}>{result.errors.length} row error(s):</p>
              <pre style={{ background: '#F3F4F6', padding: 12, borderRadius: 6, fontSize: 12, maxHeight: 240, overflow: 'auto' }}>
                {result.errors.map(e => `Row ${e.row}: ${e.error}`).join('\n')}
              </pre>
            </>
          )}
        </div>
      )}
    </>
  );
}

// ---- Performance Targets tab -------------------------------------------
//
// 6-tier annual revenue ladder per Account Manager per year. Replaces the
// older single-quarterly-quota model. Admin selects a year + a user, edits
// the 6 tier values, saves. Server upserts per (user_id, year).
//
// Tier names are golf-themed (Breakeven 5 / Bogey 10 / Par 15 / Birdie 20 /
// Eagle 25 / Hole in One 30) matching George's spreadsheet structure.

const TIERS = [
  { key: 'breakeven_value',   label: 'Breakeven 5' },
  { key: 'bogey_value',       label: 'Bogey 10' },
  { key: 'par_value',         label: 'Par 15' },
  { key: 'birdie_value',      label: 'Birdie 20' },
  { key: 'eagle_value',       label: 'Eagle 25' },
  { key: 'hole_in_one_value', label: 'Hole in One 30' }
];

const fmtTierMoney = (v) => {
  const n = Number(v || 0);
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1_000)     return '$' + Math.round(n / 1_000) + 'K';
  return '$' + n.toFixed(0);
};

function PerformanceTargetsTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');

  const reload = () => {
    setError('');
    api.listReps().then(setUsers).catch(() => {});
    api.listPerformanceTargets(year).then(setRows).catch(e => setError(e.message));
  };
  useEffect(() => { reload(); }, [year]);

  const blank = (uid) => ({
    user_id: uid || (users[0]?.id || ''),
    year,
    breakeven_value: 0, bogey_value: 0, par_value: 0,
    birdie_value: 0, eagle_value: 0, hole_in_one_value: 0
  });

  const save = async () => {
    setError('');
    try {
      const body = {};
      for (const t of TIERS) body[t.key] = Number(editing[t.key]) || 0;
      await api.setPerformanceTargets(Number(editing.user_id), Number(editing.year), body);
      setEditing(null);
      reload();
    } catch (err) { setError(err.message); }
  };

  const remove = async (userId) => {
    if (!confirm('Clear all 6 tier values for this user/year?')) return;
    try {
      await api.deletePerformanceTargets(userId, year);
      reload();
    } catch (err) { setError(err.message); }
  };

  const yearOptions = (() => {
    const cur = new Date().getFullYear();
    return [cur - 1, cur, cur + 1, cur + 2];
  })();

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ ...styles.h2, margin: 0 }}>
          Performance Targets
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            · 6-tier annual ladder per Account Manager
          </span>
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={styles.label}>Year:</label>
          <select style={{ ...styles.input, width: 120 }} value={year} onChange={e => setYear(Number(e.target.value))}>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button style={styles.btn} onClick={() => setEditing(blank())}>+ Set Targets</button>
        </div>
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}

      {editing && (
        <div style={styles.card}>
          <h2 style={styles.h2}>{rows.find(r => r.user_id === Number(editing.user_id)) ? 'Update' : 'Set'} Targets · {editing.year}</h2>
          <div style={styles.formGrid}>
            <Field label="User *">
              <select style={styles.input} value={editing.user_id} onChange={e => {
                const u = e.target.value;
                // Pre-fill from existing row if there is one
                const existing = rows.find(r => r.user_id === Number(u));
                if (existing) {
                  setEditing(prev => ({
                    ...prev,
                    user_id: u,
                    breakeven_value: existing.breakeven_value,
                    bogey_value: existing.bogey_value,
                    par_value: existing.par_value,
                    birdie_value: existing.birdie_value,
                    eagle_value: existing.eagle_value,
                    hole_in_one_value: existing.hole_in_one_value
                  }));
                } else {
                  setEditing(prev => ({ ...prev, user_id: u }));
                }
              }}>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </Field>
            <Field label="Year">
              <input type="number" style={styles.input} value={editing.year} disabled />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 12 }}>
            {TIERS.map(t => (
              <Field key={t.key} label={`${t.label} ($)`}>
                <input
                  type="number"
                  style={styles.input}
                  value={editing[t.key]}
                  onChange={e => setEditing({ ...editing, [t.key]: e.target.value })}
                />
              </Field>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={styles.btn} onClick={save}>Save</button>
            <button style={styles.btnSecondary} onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Account Manager</th>
              <th style={styles.th}>Branch</th>
              {TIERS.map(t => <th key={t.key} style={{ ...styles.th, textAlign: 'right' }}>{t.label}</th>)}
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>
                No targets set for {year}. Click <strong>+ Set Targets</strong> to add one.
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id}>
                <td style={styles.td}><strong>{r.full_name}</strong></td>
                <td style={styles.td}>{r.branch_name || ''}</td>
                {TIERS.map(t => (
                  <td key={t.key} style={{ ...styles.td, textAlign: 'right' }}>{fmtTierMoney(r[t.key])}</td>
                ))}
                <td style={styles.td}>
                  <button style={styles.btnSecondary} onClick={() => setEditing({
                    user_id: r.user_id, year: r.year,
                    breakeven_value: r.breakeven_value, bogey_value: r.bogey_value, par_value: r.par_value,
                    birdie_value: r.birdie_value, eagle_value: r.eagle_value, hole_in_one_value: r.hole_in_one_value
                  })}>Edit</button>{' '}
                  <button style={styles.btnDanger} onClick={() => remove(r.user_id)}>Clear</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---- Branch Budgets tab ------------------------------------------------
//
// Monthly Base + TA Budget + Forecast per branch per year. Per-branch
// sub-tabs (one per active branch); within each tab a 12-row monthly
// grid with four numeric columns. Bulk save per branch -- one click
// writes all 12 months in a single transaction. Controller (admin /
// leadership) updates forecasts here as the year progresses; the audit
// stamps forecast_updated_at / by on every save.

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ---- Sales Revenue (formerly Base Revenue) ---------------------------
//
// Per-rep monthly sales revenue. Admin can:
//   (a) Manually enter per-rep monthly numbers in a grid
//   (b) ⬆ Upload a Rentalman invoice export -- parses, shows preview,
//       commit replaces (year, month) data with the import
//
// Four numeric columns per (rep, month):
//   - rentals / labor / consumables : the split (drives commission)
//   - unallocated : catch-all when admin only has the monthly total
//     without a category split. Counts toward Realized $ but NOT
//     toward commission.
//
// On upload, per-row branch_id comes from the row's Loc field, so
// Branch Budget Base actual is accurate per branch (not skewed to
// the rep's home branch).

function SalesRevenueTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [reps, setReps] = useState([]);
  const [activeRepId, setActiveRepId] = useState(null);
  const [editing, setEditing] = useState({}); // user_id -> months[]
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  // Months unlocked for override this session (Set of month numbers).
  // An unlocked imported month becomes editable and saves via the
  // override endpoint (hard replace) instead of the bulk upsert.
  const [overrides, setOverrides] = useState(new Set());
  // { month, category, rentals_amount, labor_amount, consumables_amount,
  //   unallocated_amount, note } | null
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjSaving, setAdjSaving] = useState(false);

  const reload = () => {
    setError('');
    setOverrides(new Set());
    api.listSalesRevenue(year).then(rs => {
      setReps(rs);
      const ed = {};
      for (const r of rs) ed[r.user_id] = r.months.map(m => ({ ...m }));
      setEditing(ed);
      if (rs.length && !activeRepId) setActiveRepId(rs[0].user_id);
    }).catch(e => setError(e.message));
  };
  useEffect(() => { reload(); }, [year]);
  useEffect(() => { setOverrides(new Set()); }, [activeRepId]);

  const yearOpts = (() => {
    const cur = new Date().getFullYear();
    return [cur - 1, cur, cur + 1, cur + 2];
  })();

  const active = reps.find(r => r.user_id === activeRepId);
  const months = editing[activeRepId] || [];

  const updateCell = (idx, field, val) => {
    setEditing(e => ({
      ...e,
      [activeRepId]: e[activeRepId].map((m, i) => i === idx ? { ...m, [field]: val } : m)
    }));
  };

  const monthTotal = (m) =>
    Number(m.rentals_amount || 0) + Number(m.labor_amount || 0) +
    Number(m.consumables_amount || 0) + Number(m.unallocated_amount || 0);

  // Signed sum of the month's adjustment rows (Base + TA).
  const adjTotal = (m) => (m.adjustments || []).reduce((a, x) =>
    a + Number(x.rentals_amount || 0) + Number(x.labor_amount || 0) +
    Number(x.consumables_amount || 0) + Number(x.unallocated_amount || 0), 0);

  const isImportMonth = (m) => (m.sources || []).includes('rentalman_import');

  const grandTotal = months.reduce((a, m) => a + monthTotal(m) + adjTotal(m), 0);

  const save = async () => {
    if (!activeRepId) return;
    setError(''); setSaving(true); setJustSaved('');
    try {
      // Imported months never ride the bulk upsert (the server skips them
      // too) -- unlocked ones go through the override endpoint instead.
      const payload = months.filter(m => !isImportMonth(m)).map(m => ({
        month: m.month,
        rentals_amount: m.rentals_amount,
        labor_amount: m.labor_amount,
        consumables_amount: m.consumables_amount,
        unallocated_amount: m.unallocated_amount
      }));
      if (payload.length) await api.saveSalesRevenueBulk(activeRepId, year, payload);
      for (const m of months.filter(x => overrides.has(x.month))) {
        await api.overrideSalesRevenueMonth(activeRepId, year, m.month, {
          rentals_amount: m.rentals_amount,
          labor_amount: m.labor_amount,
          consumables_amount: m.consumables_amount,
          unallocated_amount: m.unallocated_amount
        });
      }
      const r = reps.find(x => x.user_id === activeRepId);
      setJustSaved(`Saved ${r?.full_name || 'rep'} · ${year}`);
      reload();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const unlockOverride = (m) => {
    const r = reps.find(x => x.user_id === activeRepId);
    const ok = window.confirm(
      `Override ${MONTH_NAMES[m.month - 1]} ${year} for ${r?.full_name || 'this rep'}?\n\n` +
      `Saving will REPLACE the month's imported revenue (every branch, Base + TA) ` +
      `with the values you type, as a single manual entry at their home branch. ` +
      `The customer drill-down keeps showing the original Rentalman detail until the month is re-uploaded.\n\n` +
      `To tweak the month while keeping the import intact, use ± Adjust instead.`
    );
    if (!ok) return;
    setOverrides(prev => new Set(prev).add(m.month));
  };

  const relockOverride = (month) => {
    setOverrides(prev => { const n = new Set(prev); n.delete(month); return n; });
    reload(); // restore the imported values in the inputs
  };

  const openAdjust = (m) => {
    const existing = (m.adjustments || []);
    const first = existing[0];
    const cat = first ? first.category : 'Base';
    const cur = existing.find(x => x.category === cat);
    setAdjustModal({
      month: m.month,
      category: cat,
      rentals_amount: cur?.rentals_amount || '',
      labor_amount: cur?.labor_amount || '',
      consumables_amount: cur?.consumables_amount || '',
      unallocated_amount: cur?.unallocated_amount || '',
      note: cur?.note || ''
    });
  };

  const adjustPickCategory = (cat) => {
    const m = months.find(x => x.month === adjustModal.month);
    const cur = (m?.adjustments || []).find(x => x.category === cat);
    setAdjustModal(a => ({
      ...a, category: cat,
      rentals_amount: cur?.rentals_amount || '',
      labor_amount: cur?.labor_amount || '',
      consumables_amount: cur?.consumables_amount || '',
      unallocated_amount: cur?.unallocated_amount || '',
      note: cur?.note || ''
    }));
  };

  const saveAdjust = async (remove) => {
    setAdjSaving(true); setError('');
    try {
      const body = remove
        ? { category: adjustModal.category, rentals_amount: 0, labor_amount: 0, consumables_amount: 0, unallocated_amount: 0 }
        : {
            category: adjustModal.category,
            rentals_amount: adjustModal.rentals_amount,
            labor_amount: adjustModal.labor_amount,
            consumables_amount: adjustModal.consumables_amount,
            unallocated_amount: adjustModal.unallocated_amount,
            note: adjustModal.note
          };
      await api.saveSalesRevenueAdjustment(activeRepId, year, adjustModal.month, body);
      setAdjustModal(null);
      reload();
    } catch (err) { setError(err.message); }
    finally { setAdjSaving(false); }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ ...styles.h2, margin: 0 }}>
          Sales Revenue
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            · Monthly per-rep sales — feeds Performance Targets
          </span>
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <UploadStatusBadge statusKey="last_rentalman_upload" refreshSignal={justSaved} />
          <button style={styles.btn} onClick={() => setShowUpload(true)}>
            ⬆ Upload Rentalman File
          </button>
          <label style={styles.label}>Year:</label>
          <select style={{ ...styles.input, width: 120 }} value={year} onChange={e => setYear(Number(e.target.value))}>
            {yearOpts.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}
      {justSaved && (
        <div style={{ background: '#D1FAE5', color: '#065F46', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          {justSaved}
        </div>
      )}

      <div style={{ background: '#FEF3C7', border: '1px solid #FBBF24', color: '#92400E', padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
        💡 <strong>Tip:</strong> Use <strong>⬆ Upload Rentalman File</strong> to bulk-import a monthly export.
        If entering manually and you only have the monthly total (no Rentals/Labor/Consumables split),
        type it into the <strong>Unallocated</strong> column — counts toward Realized $ but not commission until split.
        <div style={{ marginTop: 6, fontSize: 12 }}>
          Each Month Total now shows the <strong>Base · TA</strong> split underneath.
          If old uploads show <strong>TA $0</strong> for a month that should have TA work (08xx Loc),
          re-upload that month's Rentalman file — the importer will re-tag based on Loc.
        </div>
        <div style={{ marginTop: 6, fontSize: 12 }}>
          Imported months are locked, but can be changed three ways: <strong>re-upload</strong> a
          corrected Rentalman file (cleanest — drill-down stays in sync),
          <strong> ± Adjust</strong> (adds a signed correction on top; the import stays intact), or
          <strong> 🔓 Override</strong> (replaces the whole month with what you type).
        </div>
      </div>

      {/* Rep selector */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap', borderBottom: '1px solid #E5E7EB' }}>
        {reps.map(r => (
          <button
            key={r.user_id}
            onClick={() => setActiveRepId(r.user_id)}
            style={{
              padding: '8px 14px', border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: 13,
              background: activeRepId === r.user_id
                ? (r.is_house_account ? '#1F2937' : '#E36F2C')
                : '#fff',
              color: activeRepId === r.user_id ? '#fff' : '#374151',
              borderRadius: '6px 6px 0 0',
              borderBottom: activeRepId === r.user_id
                ? `2px solid ${r.is_house_account ? '#1F2937' : '#E36F2C'}`
                : '2px solid transparent'
            }}
          >
            {r.is_house_account ? '🏠 ' : ''}{r.full_name}
            {r.branch_name && <span style={{ marginLeft: 6, opacity: 0.75, fontWeight: 400, fontSize: 11 }}>· {r.branch_name}</span>}
          </button>
        ))}
        {!reps.length && (
          <div style={{ padding: 12, color: '#6B7280', fontSize: 13 }}>
            No eligible reps. Check that users have <em>"Show on Dashboard 'By Account Manager'"</em> enabled.
          </div>
        )}
      </div>

      {active && (
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
              {active.is_house_account ? '🏠 ' : ''}{active.full_name}
              <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>· {year}</span>
              {active.is_house_account && (
                <span style={{ marginLeft: 10, fontSize: 11, color: '#6B7280', fontWeight: 400 }}>
                  System user — captures unattributed Rentalman revenue. No commission.
                </span>
              )}
            </h3>
            {!active.is_house_account && (
              <button style={styles.btn} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : `↓ Save ${active.full_name.split(' ')[0]}`}
              </button>
            )}
          </div>

          <table style={{ ...styles.table, fontSize: 13 }}>
            <thead>
              <tr>
                <th style={styles.th}>Month</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Rentals</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Labor</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Consumables</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Unallocated</th>
                <th style={{ ...styles.th, textAlign: 'right', background: '#F9FAFB' }}>Month Total</th>
                <th style={{ ...styles.th, textAlign: 'center' }}>Source</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m, idx) => {
                const isImport = isImportMonth(m);
                const unlocked = overrides.has(m.month);
                const locked = (isImport && !unlocked) || active.is_house_account;
                const adj = adjTotal(m);
                const hasAdj = (m.adjustments || []).length > 0;
                return (
                <tr key={m.month} style={unlocked ? { background: '#FEF3C7' } : undefined}>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{MONTH_NAMES[idx]}</td>
                  <td style={styles.td}>
                    <input
                      type="number" step="0.01" min="0"
                      style={{ ...styles.input, textAlign: 'right' }}
                      value={m.rentals_amount ?? ''}
                      disabled={locked}
                      onChange={e => updateCell(idx, 'rentals_amount', e.target.value)}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number" step="0.01" min="0"
                      style={{ ...styles.input, textAlign: 'right' }}
                      value={m.labor_amount ?? ''}
                      disabled={locked}
                      onChange={e => updateCell(idx, 'labor_amount', e.target.value)}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number" step="0.01" min="0"
                      style={{ ...styles.input, textAlign: 'right' }}
                      value={m.consumables_amount ?? ''}
                      disabled={locked}
                      onChange={e => updateCell(idx, 'consumables_amount', e.target.value)}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number" step="0.01" min="0"
                      style={{ ...styles.input, textAlign: 'right' }}
                      value={m.unallocated_amount ?? ''}
                      disabled={locked}
                      onChange={e => updateCell(idx, 'unallocated_amount', e.target.value)}
                    />
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, background: '#F9FAFB' }}>
                    ${Math.round(monthTotal(m) + adj).toLocaleString()}
                    {(Number(m.base_total) > 0 || Number(m.ta_total) > 0) && (
                      <div style={{ fontSize: 10, fontWeight: 500, color: '#6B7280', marginTop: 2 }}>
                        Base ${Math.round(Number(m.base_total || 0)).toLocaleString()}
                        {' · '}
                        <span style={{ color: Number(m.ta_total) > 0 ? '#7C3AED' : '#9CA3AF' }}>
                          TA ${Math.round(Number(m.ta_total || 0)).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {hasAdj && (
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#B45309', marginTop: 2 }}>
                        ± Adj {adj < 0 ? '−' : '+'}${Math.round(Math.abs(adj)).toLocaleString()}
                      </div>
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center', fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap' }}>
                    {unlocked ? <span style={{ color: '#B45309', fontWeight: 700 }}>🔓 Override</span>
                      : (m.sources || []).length === 0 ? '—'
                      : isImport ? '📥 Rentalman'
                      : '✍️ Manual'}
                    {hasAdj && !unlocked && <span style={{ color: '#B45309' }}> ±</span>}
                    {!active.is_house_account && (
                      <div style={{ marginTop: 3, display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <button
                          type="button"
                          title="Add a signed adjustment on top of this month (import stays intact)"
                          onClick={() => openAdjust(m)}
                          style={{ fontSize: 10, padding: '2px 6px', border: '1px solid #D1D5DB', borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#374151' }}
                        >± Adjust</button>
                        {isImport && !unlocked && (
                          <button
                            type="button"
                            title="Unlock this imported month and replace it with typed values on Save"
                            onClick={() => unlockOverride(m)}
                            style={{ fontSize: 10, padding: '2px 6px', border: '1px solid #D1D5DB', borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#374151' }}
                          >🔓 Override</button>
                        )}
                        {unlocked && (
                          <button
                            type="button"
                            title="Cancel the override and restore the imported values"
                            onClick={() => relockOverride(m.month)}
                            style={{ fontSize: 10, padding: '2px 6px', border: '1px solid #FBBF24', borderRadius: 4, background: '#FEF3C7', cursor: 'pointer', color: '#92400E' }}
                          >↩ Cancel</button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
              <tr style={{ background: '#1F2937', color: '#fff', fontWeight: 700 }}>
                <td style={{ ...styles.td, color: '#fff' }}>YTD Total</td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#fff' }}>
                  ${Math.round(months.reduce((a, m) => a + Number(m.rentals_amount || 0), 0)).toLocaleString()}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#fff' }}>
                  ${Math.round(months.reduce((a, m) => a + Number(m.labor_amount || 0), 0)).toLocaleString()}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#fff' }}>
                  ${Math.round(months.reduce((a, m) => a + Number(m.consumables_amount || 0), 0)).toLocaleString()}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#fff' }}>
                  ${Math.round(months.reduce((a, m) => a + Number(m.unallocated_amount || 0), 0)).toLocaleString()}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#fff', background: '#111827' }}>
                  ${Math.round(grandTotal).toLocaleString()}
                  {(() => {
                    const baseYtd = months.reduce((a, m) => a + Number(m.base_total || 0), 0);
                    const taYtd   = months.reduce((a, m) => a + Number(m.ta_total || 0), 0);
                    const adjYtd  = months.reduce((a, m) => a + adjTotal(m), 0);
                    if (baseYtd === 0 && taYtd === 0 && adjYtd === 0) return null;
                    return (
                      <div style={{ fontSize: 11, fontWeight: 500, color: '#9CA3AF', marginTop: 3 }}>
                        Base ${Math.round(baseYtd).toLocaleString()}
                        {' · '}
                        <span style={{ color: taYtd > 0 ? '#C4B5FD' : '#6B7280' }}>
                          TA ${Math.round(taYtd).toLocaleString()}
                        </span>
                        {adjYtd !== 0 && (
                          <span style={{ color: '#FCD34D' }}>
                            {' · '}Adj {adjYtd < 0 ? '−' : '+'}${Math.round(Math.abs(adjYtd)).toLocaleString()}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td style={styles.td}></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {showUpload && (
        <SalesRevenueUploadModal
          onClose={() => setShowUpload(false)}
          onCommit={() => { setShowUpload(false); reload(); }}
        />
      )}

      {adjustModal && (
        <div style={modalBackdrop} onClick={() => !adjSaving && setAdjustModal(null)}>
          <div style={{ ...modalCard, maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 4px 0' }}>
              ± Adjust {MONTH_NAMES[adjustModal.month - 1]} {year} · {active?.full_name}
            </h3>
            <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 14px 0' }}>
              A signed amount added ON TOP of the month — the imported Rentalman data stays
              intact and the customer drill-down keeps reconciling. Use negative numbers to
              adjust down. Flows into Performance Targets, Dashboard, Branch Budgets and the
              VP scorecard like any other revenue. Setting everything to 0 removes the adjustment.
            </p>
            <div style={{ marginBottom: 10 }}>
              <label style={styles.label}>Category</label>
              <select
                style={{ ...styles.input, width: 160 }}
                value={adjustModal.category}
                onChange={e => adjustPickCategory(e.target.value)}
              >
                <option value="Base">Base</option>
                <option value="TA">TA (books to TA-DIV-800)</option>
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              {[['rentals_amount', 'Rentals'], ['labor_amount', 'Labor'],
                ['consumables_amount', 'Consumables'], ['unallocated_amount', 'Unallocated']].map(([f, label]) => (
                <div key={f}>
                  <label style={styles.label}>{label} $ (±)</label>
                  <input
                    type="number" step="0.01"
                    style={{ ...styles.input, textAlign: 'right', width: '100%' }}
                    value={adjustModal[f]}
                    onChange={e => setAdjustModal(a => ({ ...a, [f]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={styles.label}>Why (shows in the grid tooltip / audit)</label>
              <input
                type="text" maxLength={500}
                style={{ ...styles.input, width: '100%' }}
                placeholder="e.g. Credit memo #1234 not in the Rentalman export"
                value={adjustModal.note}
                onChange={e => setAdjustModal(a => ({ ...a, note: e.target.value }))}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <button
                style={{ ...styles.btnSecondary, color: '#991B1B' }}
                disabled={adjSaving}
                onClick={() => saveAdjust(true)}
              >Remove adjustment</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={styles.btnSecondary} disabled={adjSaving} onClick={() => setAdjustModal(null)}>Cancel</button>
                <button style={styles.btn} disabled={adjSaving} onClick={() => saveAdjust(false)}>
                  {adjSaving ? 'Saving…' : 'Save adjustment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Rentalman upload modal. Three steps:
//   1) Pick file -> uploads to /upload -> shows preview
//   2) Preview shows: per-rep totals, per-branch totals, months covered,
//      unmatched names. Admin reviews then clicks Commit.
//   3) Commit -> POST /commit-upload with the aggregated payload + month
//      list. Server wipes existing rows in those months + inserts.
function SalesRevenueUploadModal({ onClose, onCommit }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleUpload = async () => {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const result = await api.uploadSalesRevenue(file);
      setPreview(result);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const handleCommit = async () => {
    if (!preview) return;
    if (!confirm(`This will REPLACE all sales revenue for ${preview.summary.months_covered.length} month(s): ${preview.summary.months_covered.join(', ')}.\n\nAny existing manual entries for those months will be wiped. Continue?`)) return;
    setBusy(true); setError('');
    try {
      const result = await api.commitSalesRevenueUpload(
        preview.aggregated,
        preview.summary.months_covered,
        preview.raw
      );
      // Surface the Base/TA split from the server response so the user
      // immediately knows whether TA actually wrote. If ta_rows=0 here,
      // the file had no 08xx Loc invoices for any rep. raw_rows_archived
      // is the per-invoice archive count that powers the dashboard
      // drill-down (customer rollup, raw row list).
      alert(
        `Committed ${result.rows_inserted} aggregate rows.\n` +
        `Base: ${result.base_rows} rows · $${Math.round(result.base_amount || 0).toLocaleString()}\n` +
        `TA:   ${result.ta_rows} rows · $${Math.round(result.ta_amount || 0).toLocaleString()}\n` +
        `Raw archived: ${result.raw_rows_archived || 0} invoice lines (drill-down ready)`
      );
      onCommit();
    } catch (err) { setError(err.message); setBusy(false); }
  };

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={{ ...modalCard, maxWidth: 800 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>⬆ Upload Rentalman Sales Export</h2>
          <button style={modalClose} onClick={onClose}>✕</button>
        </div>

        {error && <div style={{ ...styles.errorBox, marginBottom: 12 }}>{error}</div>}

        {!preview && (
          <>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
              Upload the monthly Rentalman commission export (.xlsx). The parser reads each
              invoice line, groups by (rep, branch, month, category), and replaces existing
              data for any months in the file.
            </p>
            <DropZone block onFiles={files => setFile(files[0])} disabled={busy}
              title="Drop the Rentalman export here" style={{ marginBottom: 12, padding: 6 }}>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={e => setFile(e.target.files[0])}
              />
              {file && <span style={{ marginLeft: 10, fontSize: 12, color: '#374151' }}>📎 {file.name}</span>}
            </DropZone>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={styles.btnSecondary} onClick={onClose}>Cancel</button>
              <button style={styles.btn} onClick={handleUpload} disabled={!file || busy}>
                {busy ? 'Parsing…' : 'Upload + Preview'}
              </button>
            </div>
          </>
        )}

        {preview && (
          <>
            <div style={{ background: '#D1FAE5', color: '#065F46', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
              ✅ Parsed <strong>{preview.summary.rows_aggregated.toLocaleString()}</strong> rows
              ({preview.summary.rows_skipped} skipped),
              total <strong>${Math.round(preview.summary.total_amount).toLocaleString()}</strong>,
              across <strong>{preview.summary.months_covered.length}</strong> month(s):
              {' ' + preview.summary.months_covered.join(', ')}
              {Number(preview.summary.reclassified_cons_to_rental_count) > 0 && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#065F46' }}>
                  🔁 Reclassified <strong>{preview.summary.reclassified_cons_to_rental_count.toLocaleString()}</strong> rows
                  (${Math.round(preview.summary.reclassified_cons_to_rental_amount).toLocaleString()})
                  from Consumables → Rentals based on Description keyword (e.g. "Turnaround Rental Equipment").
                </div>
              )}
            </div>

            {preview.summary.unmatched_rep_names.length > 0 && (
              <div style={{ background: '#FEF3C7', border: '1px solid #FBBF24', color: '#92400E', padding: 10, borderRadius: 6, marginBottom: 16, fontSize: 12 }}>
                ⚠️ <strong>{preview.summary.unmatched_rep_names.length} rep name(s) didn't match any user</strong> and were routed to House Account:
                <div style={{ marginTop: 4, fontFamily: 'monospace' }}>{preview.summary.unmatched_rep_names.join(', ')}</div>
                <div style={{ marginTop: 4 }}>Fix by setting their sales_id under Admin → Users, then re-upload.</div>
              </div>
            )}

            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
              Per-Rep Totals
              <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 400, color: '#6B7280' }}>
                Base = non-08xx Loc · TA = 08xx Loc (books to TA-DIV-800)
              </span>
            </h3>
            <table style={{ ...styles.table, fontSize: 12, marginBottom: 16 }}>
              <thead>
                <tr>
                  <th style={styles.th}>AM</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Rentals</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Labor</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Consumables</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Base</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>TA</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {preview.rep_summary.map(r => (
                  <tr key={r.user_id} style={r.is_house ? { background: '#F3F4F6' } : {}}>
                    <td style={styles.td}>{r.is_house ? '🏠 ' : ''}{r.full_name}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${Math.round(r.rentals).toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${Math.round(r.labor).toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${Math.round(r.consumables).toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${Math.round(r.base || 0).toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right', color: Number(r.ta) > 0 ? '#7C3AED' : '#9CA3AF', fontWeight: Number(r.ta) > 0 ? 700 : 400 }}>
                      ${Math.round(r.ta || 0).toLocaleString()}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>${Math.round(r.total).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#1F2937', color: '#fff', fontWeight: 700 }}>
                  <td style={{ ...styles.td, color: '#fff' }}>TOTALS</td>
                  <td style={{ ...styles.td, color: '#fff', textAlign: 'right' }}>
                    ${Math.round(preview.rep_summary.reduce((a, r) => a + Number(r.rentals || 0), 0)).toLocaleString()}
                  </td>
                  <td style={{ ...styles.td, color: '#fff', textAlign: 'right' }}>
                    ${Math.round(preview.rep_summary.reduce((a, r) => a + Number(r.labor || 0), 0)).toLocaleString()}
                  </td>
                  <td style={{ ...styles.td, color: '#fff', textAlign: 'right' }}>
                    ${Math.round(preview.rep_summary.reduce((a, r) => a + Number(r.consumables || 0), 0)).toLocaleString()}
                  </td>
                  <td style={{ ...styles.td, color: '#fff', textAlign: 'right' }}>
                    ${Math.round(preview.summary.base_total || 0).toLocaleString()}
                  </td>
                  <td style={{ ...styles.td, color: Number(preview.summary.ta_total) > 0 ? '#C4B5FD' : '#9CA3AF', textAlign: 'right' }}>
                    ${Math.round(preview.summary.ta_total || 0).toLocaleString()}
                  </td>
                  <td style={{ ...styles.td, color: '#fff', textAlign: 'right' }}>
                    ${Math.round(preview.summary.total_amount || 0).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>

            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Per-Branch Totals</h3>
            <table style={{ ...styles.table, fontSize: 12, marginBottom: 16 }}>
              <thead>
                <tr>
                  <th style={styles.th}>Branch</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {preview.branch_summary.map(b => (
                  <tr key={b.branch_id}>
                    <td style={styles.td}>{b.code} · {b.name}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>${Math.round(b.total).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 12, borderTop: '1px solid #E5E7EB' }}>
              <button style={styles.btnSecondary} onClick={onClose} disabled={busy}>Cancel</button>
              <button style={styles.btn} onClick={handleCommit} disabled={busy}>
                {busy ? 'Committing…' : `✓ Commit (replace ${preview.summary.months_covered.length} month${preview.summary.months_covered.length === 1 ? '' : 's'})`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const modalBackdrop = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '40px 16px', zIndex: 1000, overflowY: 'auto'
};
const modalCard = {
  background: '#fff', borderRadius: 8, padding: 24,
  maxWidth: 640, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
};
const modalClose = {
  background: 'none', border: 'none', fontSize: 22, cursor: 'pointer',
  color: '#6B7280', padding: 4, lineHeight: 1
};

function BranchBudgetsTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [branches, setBranches] = useState([]);
  // activeBranch: 'corporate' = rolled-up view; otherwise the branch_id
  const [activeBranch, setActiveBranch] = useState('corporate');
  const [corporateMonth, setCorporateMonth] = useState(new Date().getMonth() + 1);
  const [editing, setEditing] = useState({}); // branch_id -> months[]
  const [error, setError] = useState('');
  const [savingBranchId, setSavingBranchId] = useState(null);
  const [justSaved, setJustSaved] = useState('');

  const reload = () => {
    setError('');
    api.listBranchBudgets(year).then(rs => {
      setBranches(rs);
      // Seed editing map -- deep copy so input edits don't mutate the source
      const ed = {};
      for (const b of rs) ed[b.branch_id] = b.months.map(m => ({ ...m }));
      setEditing(ed);
    }).catch(e => setError(e.message));
  };
  useEffect(() => { reload(); }, [year]);

  const yearOptions = (() => {
    const cur = new Date().getFullYear();
    return [cur - 1, cur, cur + 1, cur + 2];
  })();

  const setCell = (branchId, monthIdx, field, value) => {
    setEditing(prev => {
      const months = (prev[branchId] || []).map((m, i) => i === monthIdx ? { ...m, [field]: value } : m);
      return { ...prev, [branchId]: months };
    });
  };

  const saveBranch = async (branchId) => {
    setError(''); setSavingBranchId(branchId); setJustSaved('');
    try {
      // Only send the manually-editable fields. base_actual and ta_actual
      // are auto-computed server-side from rep_base_revenue and
      // projects.actual_value -- the server ignores them in the body even
      // if we sent them.
      const months = (editing[branchId] || []).map(m => ({
        month: m.month,
        base_budget: m.base_budget,
        base_forecast: m.base_forecast,
        ta_budget: m.ta_budget,
        ta_forecast: m.ta_forecast,
        stronghold_actual: m.stronghold_actual,
        accrual: m.accrual
      }));
      await api.saveBranchBudgetBulk(branchId, year, months);
      const b = branches.find(x => x.branch_id === branchId);
      setJustSaved(`Saved ${b?.branch_name || 'branch'} · ${year}`);
      reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingBranchId(null);
    }
  };

  if (!branches.length && !error) {
    return (
      <>
        <h2 style={styles.h2}>Branch Budgets</h2>
        <p style={{ color: '#6B7280' }}>Loading...</p>
      </>
    );
  }

  const active = branches.find(b => b.branch_id === activeBranch);
  const activeMonths = editing[activeBranch] || [];
  const totalRow = (key) => activeMonths.reduce((s, m) => s + Number(m[key] || 0), 0);
  // Only TA-DIV-800 counts TA in its Total. Every other branch shows TA
  // as a visual credit only (not summed into Total / Variance).
  const isTaDiv = !!active?.is_ta_div;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ ...styles.h2, margin: 0 }}>
          Branch Budgets
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            · Monthly Base + TA Budget + Forecast
          </span>
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={styles.label}>Year:</label>
          <select style={{ ...styles.input, width: 120 }} value={year} onChange={e => setYear(Number(e.target.value))}>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}
      {justSaved && (
        <div style={{ background: '#D1FAE5', color: '#065F46', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          {justSaved}
        </div>
      )}

      {/* Branch sub-tabs -- "Corporate" rolled-up view first, then one
          tab per branch. Corporate is the controller's weekly email
          layout (all branches in one table). Branch tabs are for
          data entry. */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap', borderBottom: '1px solid #E5E7EB' }}>
        <button
          key="corporate"
          onClick={() => setActiveBranch('corporate')}
          style={{
            padding: '8px 14px', border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: 13,
            background: activeBranch === 'corporate' ? '#1F2937' : '#fff',
            color: activeBranch === 'corporate' ? '#fff' : '#1F2937',
            borderRadius: '6px 6px 0 0',
            borderBottom: activeBranch === 'corporate' ? '2px solid #1F2937' : '2px solid transparent'
          }}
          title="All-branches rolled-up view (matches controller's weekly email)"
        >
          🏢 Corporate
        </button>
        {branches.map(b => (
          <button
            key={b.branch_id}
            onClick={() => setActiveBranch(b.branch_id)}
            style={{
              padding: '8px 14px', border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: 13,
              background: activeBranch === b.branch_id ? '#E36F2C' : '#fff',
              color: activeBranch === b.branch_id ? '#fff' : '#374151',
              borderRadius: '6px 6px 0 0',
              borderBottom: activeBranch === b.branch_id ? '2px solid #E36F2C' : '2px solid transparent'
            }}
          >
            {b.branch_code} · {b.branch_name}
          </button>
        ))}
      </div>

      {/* Corporate view -- all branches in one table, matches controller's email */}
      {activeBranch === 'corporate' && <CorporateBranchSummary
        branches={branches}
        editing={editing}
        year={year}
        selectedMonth={corporateMonth}
        onMonthChange={setCorporateMonth}
      />}

      {active && (
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ ...styles.h2, margin: 0 }}>
              {active.branch_name} <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13 }}>· {year}</span>
            </h2>
            <button
              style={styles.btn}
              onClick={() => saveBranch(activeBranch)}
              disabled={savingBranchId === activeBranch}
            >
              {savingBranchId === activeBranch ? 'Saving…' : `↓ Save ${active.branch_name}`}
            </button>
          </div>

          {/* Planning table -- Budget + Forecast for Base and TA, plus the
              computed Actual + two variance columns (vs Budget = original
              commitment, vs Forecast = revised expectation) for months
              that have closed. Actual = base_actual + stronghold + TA +
              accrual (the Grand Total from the Reporting table below).
              Both variances use the same sign convention: Actual − Target,
              positive (beat) green, negative (missed) red. Renders "—"
              when no actual yet so future months don't show misleading
              -100% variances. */}
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', margin: '16px 0 8px 0' }}>
            Planning <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 12 }}>· Budget + Forecast + Actual + Variance (vs Budget &amp; vs Forecast)</span>
          </h3>
          <table style={{ ...styles.table, fontSize: 13 }}>
            <thead>
              <tr>
                <th style={styles.th}>Month</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Base Budget</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>TA Budget</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Total Budget</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Base Forecast</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>TA Forecast</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Total Forecast</th>
                <th style={{ ...styles.th, textAlign: 'right', background: '#F0F9FF' }}>Actual</th>
                <th style={{ ...styles.th, textAlign: 'right', background: '#F0F9FF' }} title="Actual − Total Budget. Positive = beat the original commitment.">vs Budget</th>
                <th style={{ ...styles.th, textAlign: 'right', background: '#F0F9FF' }} title="Actual − Total Forecast. Positive = beat the revised expectation.">vs Forecast</th>
              </tr>
            </thead>
            <tbody>
              {activeMonths.map((m, idx) => {
                // TA Budget + TA Forecast only count toward the branch total
                // for TA-DIV-800; everywhere else they're informational.
                const tBud = Number(m.base_budget) + (isTaDiv ? Number(m.ta_budget) : 0);
                const tFc  = Number(m.base_forecast) + (isTaDiv ? Number(m.ta_forecast) : 0);
                // Actual = same Grand Total used by the Reporting table.
                // Non-TA-DIV branches exclude TA from the total.
                const actual = Number(m.base_actual) + Number(m.stronghold_actual) +
                               (isTaDiv ? Number(m.ta_actual) : 0) + Number(m.accrual);
                const vsBudget   = actual - tBud;
                const vsForecast = actual - tFc;
                const hasActuals = actual !== 0;
                // Color helper: beat target = green; within -10% = amber; worse = red.
                const varColor = (v, target) =>
                  !hasActuals ? '#9CA3AF'
                  : v >= 0 ? '#065F46'
                  : (target > 0 && v >= -target * 0.1) ? '#92400E'
                  : '#991B1B';
                const vsBudColor = varColor(vsBudget, tBud);
                const vsFcColor  = varColor(vsForecast, tFc);
                const fmtV = (v, target) => !hasActuals ? '—'
                  : target === 0 ? '—'
                  : (v >= 0 ? '+' : '-') + '$' + Math.round(Math.abs(v)).toLocaleString();
                return (
                  <tr key={m.month}>
                    <td style={styles.td}><strong>{MONTH_NAMES[idx]}</strong></td>
                    <td style={styles.td}>
                      <input type="number" step="0.01" min="0"
                        style={{ ...styles.input, textAlign: 'right', padding: '4px 6px' }}
                        value={m.base_budget}
                        onChange={e => setCell(activeBranch, idx, 'base_budget', e.target.value)} />
                    </td>
                    <td style={styles.td}>
                      <input type="number" step="0.01" min="0"
                        style={{ ...styles.input, textAlign: 'right', padding: '4px 6px',
                                 ...(isTaDiv ? {} : { fontStyle: 'italic', color: '#9CA3AF' }) }}
                        title={isTaDiv ? '' : 'Visual credit only -- not in branch total. TA revenue books to TA-DIV-800.'}
                        value={m.ta_budget}
                        onChange={e => setCell(activeBranch, idx, 'ta_budget', e.target.value)} />
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', color: '#6B7280' }}>${tBud.toLocaleString()}</td>
                    <td style={styles.td}>
                      <input type="number" step="0.01" min="0"
                        style={{ ...styles.input, textAlign: 'right', padding: '4px 6px' }}
                        value={m.base_forecast}
                        onChange={e => setCell(activeBranch, idx, 'base_forecast', e.target.value)} />
                    </td>
                    <td style={styles.td}>
                      <input type="number" step="0.01" min="0"
                        style={{ ...styles.input, textAlign: 'right', padding: '4px 6px',
                                 ...(isTaDiv ? {} : { fontStyle: 'italic', color: '#9CA3AF' }) }}
                        title={isTaDiv ? '' : 'Visual credit only -- not in branch total.'}
                        value={m.ta_forecast}
                        onChange={e => setCell(activeBranch, idx, 'ta_forecast', e.target.value)} />
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', color: '#6B7280', fontWeight: 600 }}>${tFc.toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, background: '#F0F9FF', color: hasActuals ? '#1F2937' : '#9CA3AF' }}>
                      {hasActuals ? '$' + Math.round(actual).toLocaleString() : '—'}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, background: '#F0F9FF', color: vsBudColor }}>
                      {fmtV(vsBudget, tBud)}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, background: '#F0F9FF', color: vsFcColor }}>
                      {fmtV(vsForecast, tFc)}
                    </td>
                  </tr>
                );
              })}
              {(() => {
                const ytdActual = totalRow('base_actual') + totalRow('stronghold_actual') +
                                  (isTaDiv ? totalRow('ta_actual') : 0) + totalRow('accrual');
                const ytdBudget   = totalRow('base_budget')   + (isTaDiv ? totalRow('ta_budget')   : 0);
                const ytdForecast = totalRow('base_forecast') + (isTaDiv ? totalRow('ta_forecast') : 0);
                const ytdVsBudget   = ytdActual - ytdBudget;
                const ytdVsForecast = ytdActual - ytdForecast;
                const hasYtdActuals = ytdActual !== 0;
                const ytdVarColor = (v, target) => !hasYtdActuals ? '#9CA3AF'
                  : v >= 0 ? '#065F46'
                  : (target > 0 && v >= -target * 0.1) ? '#92400E'
                  : '#991B1B';
                const fmtYtd = (v, target) => !hasYtdActuals ? '—'
                  : target === 0 ? '—'
                  : (v >= 0 ? '+' : '-') + '$' + Math.round(Math.abs(v)).toLocaleString();
                return (
                  <tr style={{ background: '#F3F4F6', fontWeight: 700 }}>
                    <td style={styles.td}>YTD Total</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${totalRow('base_budget').toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right', ...(isTaDiv ? {} : { color: '#9CA3AF', fontStyle: 'italic' }) }}>
                      ${totalRow('ta_budget').toLocaleString()}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${ytdBudget.toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${totalRow('base_forecast').toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right', ...(isTaDiv ? {} : { color: '#9CA3AF', fontStyle: 'italic' }) }}>
                      ${totalRow('ta_forecast').toLocaleString()}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${ytdForecast.toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right', background: '#E0F2FE' }}>
                      {hasYtdActuals ? '$' + Math.round(ytdActual).toLocaleString() : '—'}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', background: '#E0F2FE', color: ytdVarColor(ytdVsBudget, ytdBudget) }}>
                      {fmtYtd(ytdVsBudget, ytdBudget)}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', background: '#E0F2FE', color: ytdVarColor(ytdVsForecast, ytdForecast) }}>
                      {fmtYtd(ytdVsForecast, ytdForecast)}
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>

          {/* Reporting table -- matches the controller's weekly email
              layout. Base + TA are AUTO-COMPUTED from rep-level data
              (Sales Revenue tab + Project Tracker). Stronghold + Accrual
              are manually entered branch-level fields. FR was dropped
              2026-05-13 -- no longer tracked. */}
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', margin: '24px 0 8px 0' }}>
            Actuals + Reporting <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 12 }}>· Base + TA auto-computed from rep data; Stronghold + Accrual entered here</span>
          </h3>
          <table style={{ ...styles.table, fontSize: 13 }}>
            <thead>
              <tr>
                <th style={styles.th}>Month</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Base</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Stronghold</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Turnaround</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>TOTAL</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Accrual</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>GRAND TOTAL</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Forecast</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {activeMonths.map((m, idx) => {
                // For non-TA-DIV branches, TA is a visual credit only --
                // not included in Total, Grand, Forecast, or Balance math.
                const total      = Number(m.base_actual) + Number(m.stronghold_actual) +
                                   (isTaDiv ? Number(m.ta_actual) : 0);
                const grandTotal = total + Number(m.accrual);
                const forecast   = Number(m.base_forecast) + (isTaDiv ? Number(m.ta_forecast) : 0);
                const balance    = forecast - grandTotal;
                const balanceColor = forecast === 0 ? '#9CA3AF'
                                  : balance >= 0 ? '#065F46'
                                  : balance >= -forecast * 0.1 ? '#92400E'
                                  : '#991B1B';
                const taCellStyle = isTaDiv
                  ? { ...styles.td, textAlign: 'right', color: '#374151', fontWeight: 600, background: '#F3F4F6' }
                  : { ...styles.td, textAlign: 'right', color: '#9CA3AF', fontWeight: 600, fontStyle: 'italic', background: '#F9FAFB' };
                const taTitle = isTaDiv
                  ? "Auto-computed from Rentalman import. Sum of TA-category sales (08xx Loc) booked to this branch."
                  : "Visual credit only -- this branch's reps' TA contribution. Does NOT roll into Total. Actual TA revenue books to TA-DIV-800.";
                return (
                  <tr key={m.month}>
                    <td style={styles.td}><strong>{MONTH_NAMES[idx]}</strong></td>
                    <td style={{ ...styles.td, textAlign: 'right', color: '#374151', fontWeight: 600, background: '#F3F4F6' }}
                        title="Auto-computed from Rentalman import (Admin → Sales Revenue). Sum of every rep's Base-category sales billed at this branch's Loc codes (e.g. 04xx for Gonzales), including House Account. Counts in branch TOTAL.">
                      ${Math.round(Number(m.base_actual)).toLocaleString()}
                    </td>
                    <td style={styles.td}>
                      <input type="number" step="0.01" min="0"
                        style={{ ...styles.input, textAlign: 'right', padding: '4px 6px', background: '#F0F9FF' }}
                        value={m.stronghold_actual}
                        onChange={e => setCell(activeBranch, idx, 'stronghold_actual', e.target.value)} />
                    </td>
                    <td style={taCellStyle} title={taTitle}>
                      ${Math.round(Number(m.ta_actual)).toLocaleString()}
                      {!isTaDiv && <span style={{ marginLeft: 4, fontSize: 10 }}>*</span>}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', color: '#374151', fontWeight: 600 }}>${total.toLocaleString()}</td>
                    <td style={styles.td}>
                      <input type="number" step="0.01"
                        style={{ ...styles.input, textAlign: 'right', padding: '4px 6px', background: '#FEF3C7' }}
                        value={m.accrual}
                        onChange={e => setCell(activeBranch, idx, 'accrual', e.target.value)} />
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', color: '#1F2937', fontWeight: 700 }}>${grandTotal.toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right', color: '#6B7280' }}>${forecast.toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right', color: balanceColor, fontWeight: 600 }}>
                      {forecast === 0 ? '—' : (balance >= 0 ? '+' : '') + '$' + Math.abs(balance).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
              {/* YTD totals row -- TA excluded from Total/Grand/Forecast
                  for non-TA-DIV branches (visual credit only). */}
              {(() => {
                const ytdTotal      = totalRow('base_actual') + totalRow('stronghold_actual') +
                                      (isTaDiv ? totalRow('ta_actual') : 0);
                const ytdAccrual    = totalRow('accrual');
                const ytdGrand      = ytdTotal + ytdAccrual;
                const ytdForecast   = totalRow('base_forecast') + (isTaDiv ? totalRow('ta_forecast') : 0);
                const ytdBalance    = ytdForecast - ytdGrand;
                const ytdTaStyle = isTaDiv
                  ? { ...styles.td, textAlign: 'right' }
                  : { ...styles.td, textAlign: 'right', color: '#9CA3AF', fontStyle: 'italic' };
                return (
                  <tr style={{ background: '#F3F4F6', fontWeight: 700 }}>
                    <td style={styles.td}>YTD Total</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${totalRow('base_actual').toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${totalRow('stronghold_actual').toLocaleString()}</td>
                    <td style={ytdTaStyle}>
                      ${totalRow('ta_actual').toLocaleString()}
                      {!isTaDiv && <span style={{ marginLeft: 4, fontSize: 10 }}>*</span>}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${ytdTotal.toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{ytdAccrual < 0 ? '-' : ''}${Math.abs(ytdAccrual).toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${ytdGrand.toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>${ytdForecast.toLocaleString()}</td>
                    <td style={{ ...styles.td, textAlign: 'right', color: ytdBalance >= 0 ? '#065F46' : '#991B1B' }}>
                      {ytdForecast === 0 ? '—' : (ytdBalance >= 0 ? '+' : '') + '$' + Math.abs(ytdBalance).toLocaleString()}
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
          {!isTaDiv && (
            <p style={{ fontSize: 11, color: '#6B7280', margin: '6px 0 0 0', fontStyle: 'italic' }}>
              <span style={{ fontWeight: 600 }}>*</span> Turnaround column shows
              this branch's reps' TA contribution (visual credit). Actual TA
              revenue books to <strong>TA-DIV-800</strong> and is excluded
              from this branch's TOTAL. Reps still get full commission on
              TA work via Performance Targets.
            </p>
          )}

          {active.months.some(m => m.forecast_updated_at) && (
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 8 }}>
              Last forecast update: {active.months
                .filter(m => m.forecast_updated_at)
                .sort((a, b) => new Date(b.forecast_updated_at) - new Date(a.forecast_updated_at))[0]?.forecast_updated_by_name || '—'}
              {' '}on{' '}
              {new Date(
                active.months
                  .filter(m => m.forecast_updated_at)
                  .sort((a, b) => new Date(b.forecast_updated_at) - new Date(a.forecast_updated_at))[0]
                  .forecast_updated_at
              ).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// Corporate consolidated view -- all branches in one table, the way the
// controller sends out her weekly email. Two tables stacked:
//   1. Selected Month -- pick which month to display
//   2. YTD through Selected Month -- accumulated
// Each table has one row per branch + a TOTAL row at the bottom, and the
// same 10 columns as the controller's email:
//   Branch | Base | Stronghold | FR | Turnaround | TOTAL | Accrual | GRAND TOTAL | Forecast | Balance
function CorporateBranchSummary({ branches, editing, year, selectedMonth, onMonthChange }) {
  const monthOptions = MONTH_NAMES;
  // Build a per-branch summary for either a single month or the YTD range.
  // Per business rule: only TA-DIV-800 counts TA in its branch total +
  // forecast. Every other branch shows TA as a visual credit (the `ta`
  // field is still computed and rendered, but excluded from total/forecast
  // math). The corporate TOTAL row sums each branch's total -- so TA
  // appears in the company-wide grand total exactly once via TA-DIV-800.
  const summarize = (branch, fromMonth, toMonth) => {
    const months = (editing[branch.branch_id] || branch.months || []).slice(fromMonth - 1, toMonth);
    const agg = months.reduce((a, m) => ({
      base:       a.base       + Number(m.base_actual || 0),
      stronghold: a.stronghold + Number(m.stronghold_actual || 0),
      ta:         a.ta         + Number(m.ta_actual || 0),
      accrual:    a.accrual    + Number(m.accrual || 0),
      forecast_base: a.forecast_base + Number(m.base_forecast || 0),
      forecast_ta:   a.forecast_ta   + Number(m.ta_forecast || 0)
    }), { base: 0, stronghold: 0, ta: 0, accrual: 0, forecast_base: 0, forecast_ta: 0 });
    const isTaDiv = !!branch.is_ta_div;
    agg.total = agg.base + agg.stronghold + (isTaDiv ? agg.ta : 0);
    agg.grand = agg.total + agg.accrual;
    agg.forecast = agg.forecast_base + (isTaDiv ? agg.forecast_ta : 0);
    agg.balance = agg.forecast - agg.grand;
    agg.is_ta_div = isTaDiv;
    return agg;
  };

  const fmt = (n) => (n < 0 ? '-$' : '$') + Math.abs(Math.round(Number(n) || 0)).toLocaleString();
  const balanceColor = (b, f) => f === 0 ? '#9CA3AF'
                              : b >= 0 ? '#065F46'
                              : b >= -f * 0.1 ? '#92400E'
                              : '#991B1B';

  const renderTable = (label, fromMonth, toMonth) => {
    // Per-branch rows + TOTAL row
    const rows = branches.map(b => ({ branch: b, agg: summarize(b, fromMonth, toMonth) }));
    const totals = rows.reduce((a, r) => ({
      base:       a.base       + r.agg.base,
      stronghold: a.stronghold + r.agg.stronghold,
      ta:         a.ta         + r.agg.ta,
      total:      a.total      + r.agg.total,
      accrual:    a.accrual    + r.agg.accrual,
      grand:      a.grand      + r.agg.grand,
      forecast:   a.forecast   + r.agg.forecast,
      balance:    a.balance    + r.agg.balance
    }), { base: 0, stronghold: 0, ta: 0, total: 0, accrual: 0, grand: 0, forecast: 0, balance: 0 });
    return (
      <div style={{ marginTop: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', margin: '0 0 8px 0' }}>{label}</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...styles.table, fontSize: 12, minWidth: 900 }}>
            <thead>
              <tr>
                <th style={styles.th}>Branch</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Base</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Stronghold</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Turnaround</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>TOTAL</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Accrual</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>GRAND TOTAL</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Forecast</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Balance to Forecast</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ branch, agg }) => (
                <tr key={branch.branch_id}>
                  <td style={styles.td}><strong>{branch.branch_name}</strong></td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(agg.base)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{fmt(agg.stronghold)}</td>
                  <td style={{ ...styles.td, textAlign: 'right',
                                ...(agg.is_ta_div ? {} : { color: '#9CA3AF', fontStyle: 'italic' }) }}
                      title={agg.is_ta_div ? '' : 'Visual credit only -- not in this branch total. TA revenue books to TA-DIV-800.'}>
                    {fmt(agg.ta)}{!agg.is_ta_div && <span style={{ marginLeft: 4, fontSize: 9 }}>*</span>}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{fmt(agg.total)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', color: agg.accrual < 0 ? '#991B1B' : '#6B7280' }}>{fmt(agg.accrual)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>{fmt(agg.grand)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', color: '#6B7280' }}>{fmt(agg.forecast)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: balanceColor(agg.balance, agg.forecast) }}>
                    {agg.forecast === 0 ? '—' : (agg.balance >= 0 ? '+' : '') + fmt(Math.abs(agg.balance))}
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#1F2937', color: '#fff', fontWeight: 700 }}>
                <td style={{ ...styles.td, color: '#fff' }}>TOTAL</td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#fff' }}>{fmt(totals.base)}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#fff' }}>{fmt(totals.stronghold)}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#fff' }}>{fmt(totals.ta)}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#fff' }}>{fmt(totals.total)}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: totals.accrual < 0 ? '#FCA5A5' : '#fff' }}>{fmt(totals.accrual)}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#fff' }}>{fmt(totals.grand)}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#fff' }}>{fmt(totals.forecast)}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: totals.balance >= 0 ? '#86EFAC' : '#FCA5A5' }}>
                  {totals.forecast === 0 ? '—' : (totals.balance >= 0 ? '+' : '') + fmt(Math.abs(totals.balance))}
                </td>
              </tr>
            </tbody>
          </table>
          <p style={{ fontSize: 11, color: '#6B7280', margin: '6px 0 0 0', fontStyle: 'italic' }}>
            <span style={{ fontWeight: 600 }}>*</span> Turnaround value shown
            on non-TA-DIV branches is a visual credit only -- those reps'
            TA contribution. Actual TA revenue books to <strong>TA-DIV-800</strong>
            and is captured there in the company TOTAL.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 4 }}>
        <h2 style={{ ...styles.h2, margin: 0 }}>
          🏢 Corporate Roll-Up
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            · all branches consolidated · {year}
          </span>
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={styles.label}>Month:</label>
          <select style={{ ...styles.input, width: 140 }} value={selectedMonth} onChange={e => onMonthChange(Number(e.target.value))}>
            {monthOptions.map((name, i) => <option key={i} value={i + 1}>{name}</option>)}
          </select>
        </div>
      </div>
      <p style={{ color: '#6B7280', fontSize: 12, marginTop: 0 }}>
        Matches the controller's weekly email. Edit numbers under the per-branch tabs; this view updates as you type.
      </p>

      {renderTable(`${monthOptions[selectedMonth - 1]} ${year}`, selectedMonth, selectedMonth)}
      {renderTable(`YTD through ${monthOptions[selectedMonth - 1]}`, 1, selectedMonth)}
    </div>
  );
}

// ---- Commissions tab ---------------------------------------------------
//
// Three sub-views:
//   1. Per-rep summary -- Billed / Earned / Outstanding / Paid / Owed
//   2. Reconciliation -- gaps between the commission lines (Sales Revenue) and
//      the RentalMan invoice feed (paid status)
//   3. Payments ledger -- record commission paid to a rep, see history
//
// Earned = commission $ on PAID invoices (status PD in the RentalMan All
// Invoices feed, rm_all_invoices, loaded every 30 minutes; see
// routes/commissions.js). Owed = Earned − Sum(commission_payments).

function CommissionsTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [view, setView] = useState('summary'); // 'summary' | 'reconcile' | 'payments'
  const [summary, setSummary] = useState(null);
  const [recon, setRecon] = useState(null);
  const [error, setError] = useState('');
  const [showPayModal, setShowPayModal] = useState(null); // { user_id, full_name, owed }

  const yearOpts = (() => {
    const cur = new Date().getFullYear();
    return [cur - 1, cur, cur + 1];
  })();

  const reload = () => {
    setError('');
    if (view === 'summary') {
      api.commissionsSummary(year).then(setSummary).catch(e => setError(e.message));
    } else if (view === 'reconcile') {
      api.commissionsReconciliation(year).then(setRecon).catch(e => setError(e.message));
    }
  };
  useEffect(reload, [year, view]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ ...styles.h2, margin: 0 }}>
          Commissions
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            · Paid-invoice based · Earned = commission on PD invoices, Paid = ledger of payouts
          </span>
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <CommissionsFeedStatus refreshSignal={view + year} />
          <label style={styles.label}>Year:</label>
          <select style={{ ...styles.input, width: 100 }} value={year} onChange={e => setYear(Number(e.target.value))}>
            {yearOpts.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #E5E7EB', marginBottom: 12 }}>
        {[
          { v: 'summary',   label: '💰 Per-Rep Summary' },
          { v: 'reconcile', label: '🔍 Reconciliation' },
          { v: 'payments',  label: '🧾 Payments Ledger' }
        ].map(t => {
          const active = view === t.v;
          return (
            <button
              key={t.v}
              type="button"
              onClick={() => setView(t.v)}
              style={{
                padding: '8px 16px', border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 600,
                background: active ? '#1F2937' : '#fff',
                color: active ? '#fff' : '#374151'
              }}
            >{t.label}</button>
          );
        })}
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {view === 'summary' && (
        <CommissionsSummaryView
          rows={summary}
          year={year}
          onRecordPayment={(rep) => setShowPayModal(rep)}
        />
      )}
      {view === 'reconcile' && <CommissionsReconcileView data={recon} year={year} />}
      {view === 'payments' && <CommissionsPaymentsView year={year} />}

      {showPayModal && (
        <RecordPaymentModal
          rep={showPayModal}
          onClose={() => setShowPayModal(null)}
          onSaved={() => { setShowPayModal(null); reload(); }}
        />
      )}
    </>
  );
}

// Where Earned's paid status comes from and how old it is. The feed loads on
// its own (Admin -> Backup -> RentalMan Invoices Sync), so this replaces the
// old upload badge: a stale or failed feed means Earned is as of the pull date.
function CommissionsFeedStatus({ refreshSignal }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.rmInvoiceSyncStatus().then(setStatus).catch(() => setStatus(null));
  }, [refreshSignal]);
  if (!status) return null;
  const chip = !status.configured ? { label: 'not configured', bg: '#F3F4F6', fg: '#4B5563' }
    : status.last_status === 'error' ? { label: 'failed', bg: '#FEE2E2', fg: '#991B1B' }
    : status.stale ? { label: 'stale', bg: '#FEF3C7', fg: '#92400E' }
    : { label: 'current', bg: '#D1FAE5', fg: '#065F46' };
  return (
    <span
      title={'Earned reads each invoice\'s status (PD = paid) from the RentalMan All Invoices report, loaded every 30 minutes. '
        + 'Status and Run now: Admin → Backup → RentalMan Invoices Sync.'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6B7280' }}
    >
      Paid status: RentalMan feed{status.report_modified ? ` · pulled ${fmtDateTime(status.report_modified)}` : ''}
      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: 0.5, background: chip.bg, color: chip.fg }}>{chip.label}</span>
    </span>
  );
}

function CommissionsSummaryView({ rows, year, onRecordPayment }) {
  if (!rows) return <div style={{ padding: 20, color: '#6B7280' }}>Loading…</div>;
  if (!rows.length) return (
    <div style={{ padding: 24, textAlign: 'center', color: '#6B7280', background: '#F9FAFB', borderRadius: 8 }}>
      No commission data yet for {year}. Commit the Rentalman file on the Sales Revenue tab to populate.
    </div>
  );
  const totals = rows.reduce((a, r) => ({
    billed: a.billed + Number(r.billed),
    earned: a.earned + Number(r.earned),
    outstanding: a.outstanding + Number(r.outstanding),
    paid_to_rep: a.paid_to_rep + Number(r.paid_to_rep),
    owed: a.owed + Number(r.owed)
  }), { billed: 0, earned: 0, outstanding: 0, paid_to_rep: 0, owed: 0 });

  return (
    <div style={styles.card}>
      <table style={{ ...styles.table, fontSize: 13 }}>
        <thead>
          <tr>
            <th style={styles.th}>Account Manager</th>
            <th style={{ ...styles.th, textAlign: 'right' }} title="Rates: rentals / labor / consumables">Rates</th>
            <th style={{ ...styles.th, textAlign: 'right' }} title="Commission $ on every billed line, regardless of paid status">Billed</th>
            <th style={{ ...styles.th, textAlign: 'right' }} title="Commission $ on lines whose invoice is PAID (PD)">Earned</th>
            <th style={{ ...styles.th, textAlign: 'right' }} title="Commission $ on lines whose invoice is open (not PD yet)">Outstanding</th>
            <th style={{ ...styles.th, textAlign: 'right' }} title="Sum of commission payments recorded for this rep YTD">Paid to Rep</th>
            <th style={{ ...styles.th, textAlign: 'right' }} title="Earned − Paid = what we still owe">Owed</th>
            <th style={{ ...styles.th, textAlign: 'center' }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.user_id}>
              <td style={{ ...styles.td, fontWeight: 600 }}>{r.full_name}</td>
              <td style={{ ...styles.td, textAlign: 'right', fontSize: 11, color: '#6B7280' }}>
                {Number(r.commission_rentals_pct || 0)}/{Number(r.commission_labor_pct || 0)}/{Number(r.commission_consumables_pct || 0)}%
              </td>
              <td style={{ ...styles.td, textAlign: 'right', color: '#6B7280' }}>${Math.round(r.billed).toLocaleString()}</td>
              <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, color: '#065F46' }}>${Math.round(r.earned).toLocaleString()}</td>
              <td style={{ ...styles.td, textAlign: 'right', color: '#92400E' }}>${Math.round(r.outstanding).toLocaleString()}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>${Math.round(r.paid_to_rep).toLocaleString()}</td>
              <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, color: r.owed > 0 ? '#991B1B' : '#065F46' }}>
                ${Math.round(r.owed).toLocaleString()}
              </td>
              <td style={{ ...styles.td, textAlign: 'center' }}>
                <button
                  style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px' }}
                  onClick={() => onRecordPayment({ user_id: r.user_id, full_name: r.full_name, owed: r.owed })}
                >+ Record Payment</button>
              </td>
            </tr>
          ))}
          <tr style={{ background: '#1F2937', color: '#fff', fontWeight: 700 }}>
            <td style={{ ...styles.td, color: '#fff' }}>TOTALS · {year}</td>
            <td style={styles.td}></td>
            <td style={{ ...styles.td, textAlign: 'right', color: '#fff' }}>${Math.round(totals.billed).toLocaleString()}</td>
            <td style={{ ...styles.td, textAlign: 'right', color: '#A7F3D0' }}>${Math.round(totals.earned).toLocaleString()}</td>
            <td style={{ ...styles.td, textAlign: 'right', color: '#FBBF24' }}>${Math.round(totals.outstanding).toLocaleString()}</td>
            <td style={{ ...styles.td, textAlign: 'right', color: '#fff' }}>${Math.round(totals.paid_to_rep).toLocaleString()}</td>
            <td style={{ ...styles.td, textAlign: 'right', color: totals.owed > 0 ? '#FCA5A5' : '#A7F3D0' }}>
              ${Math.round(totals.owed).toLocaleString()}
            </td>
            <td style={styles.td}></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function CommissionsReconcileView({ data, year }) {
  if (!data) return <div style={{ padding: 20, color: '#6B7280' }}>Loading…</div>;
  const c = data.counts || {};
  const n = (v) => Number(v || 0).toLocaleString();
  const money = (v) => `$${Math.round(Number(v || 0)).toLocaleString()}`;
  // The server returns the 200 biggest rows per list; 50 are shown.
  const SHOWN = 50;
  const countNote = (total) => `${n(total)} ${Number(total) === 1 ? 'invoice' : 'invoices'}${total > SHOWN ? `, ${SHOWN} biggest shown` : ''}`;
  // One row per invoice sequence under one account; invoice # alone repeats.
  const idKey = (acct, inv, seq) => `${acct ?? ''}|${inv ?? ''}|${seq ?? ''}`;
  const mono = { ...styles.td, fontFamily: 'monospace' };
  const right = { ...styles.th, textAlign: 'right' };
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={styles.kpiCard} title="Commission lines whose invoice (account, invoice # and sequence) is in the RentalMan feed. A line that is not reads Outstanding.">
          <div style={styles.kpiLabel}>Lines matched</div>
          <div style={styles.kpiValue}>{n(c.lines_in_feed)} of {n(c.lines_total)}</div>
          <div style={styles.kpiSubtext}>commission lines found in the feed · {year}</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiLabel}>Rentalman invoices</div>
          <div style={styles.kpiValue}>{n(c.rentalman_invoices)}</div>
          <div style={styles.kpiSubtext}>account · Inv # · Seq with commission lines · {year}</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiLabel}>Feed invoices</div>
          <div style={styles.kpiValue}>{n(c.feed_invoices)}</div>
          <div style={styles.kpiSubtext}>{n(c.feed_paid)} paid (PD) · {year}</div>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px 0' }}>
          ⚠️ Missing from the invoice feed
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            · Commission lines with no invoice in the RentalMan feed, so they read Outstanding ({countNote(c.missing_from_feed)})
          </span>
        </h3>
        {data.missing_from_feed.length === 0 ? (
          <div style={{ color: '#065F46', fontSize: 13 }}>✅ Every billed line has its invoice in the feed.</div>
        ) : (
          <table style={{ ...styles.table, fontSize: 12 }}>
            <thead><tr>
              <th style={styles.th}>Account #</th>
              <th style={styles.th}>Inv #</th>
              <th style={styles.th}>Seq</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>Inv Date</th>
              <th style={right}>Lines</th>
              <th style={right}>Billed Total</th>
            </tr></thead>
            <tbody>
              {data.missing_from_feed.slice(0, SHOWN).map(r => (
                <tr key={idKey(r.customer_no, r.inv_no, r.seq_no)}>
                  <td style={mono}>{r.customer_no || '—'}</td>
                  <td style={mono}>{r.inv_no || '—'}</td>
                  <td style={mono}>{r.seq_no ?? '—'}</td>
                  <td style={styles.td}>{r.customer_name || '—'}</td>
                  <td style={styles.td}>{r.inv_date || '—'}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{r.line_count}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{money(r.billed_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px 0' }}>
          ⚠️ Missing from Rentalman
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            · Invoices in the feed with no commission lines ({countNote(c.missing_from_rentalman)})
          </span>
        </h3>
        {data.missing_from_rentalman.length === 0 ? (
          <div style={{ color: '#065F46', fontSize: 13 }}>✅ Every invoice in the feed has commission lines.</div>
        ) : (
          <table style={{ ...styles.table, fontSize: 12 }}>
            <thead><tr>
              <th style={styles.th}>Account #</th>
              <th style={styles.th}>Inv #</th>
              <th style={styles.th}>Seq</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th} title="RentalMan salesman number, and the user whose Sales ID matches it">Sales Rep</th>
              <th style={right}>Ex-tax $</th>
            </tr></thead>
            <tbody>
              {data.missing_from_rentalman.slice(0, SHOWN).map(r => (
                <tr key={idKey(r.account_no, r.invoice_no, r.invoice_seq)}>
                  <td style={mono}>{r.account_no || '—'}</td>
                  <td style={mono}>{r.invoice_no}</td>
                  <td style={mono}>{r.invoice_seq}</td>
                  <td style={styles.td}>{r.customer_name || '—'}</td>
                  <td style={styles.td}>{r.invoice_date || '—'}</td>
                  <td style={styles.td}>{r.invoice_status || '—'}</td>
                  <td style={styles.td}>{r.rep_name ? `${r.rep_name} (${r.sales_rep})` : (r.sales_rep || '—')}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{money(r.amount_ex_tax)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px 0' }}>
          ⚠️ Lines exceed invoice
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            · Commission lines add up to more than the invoice's ex-tax amount: a line counted twice, or commission on a credit ({countNote(c.lines_exceed_invoice)})
          </span>
        </h3>
        {data.lines_exceed_invoice.length === 0 ? (
          <div style={{ color: '#065F46', fontSize: 13 }}>✅ No invoice has more commission lines than it billed.</div>
        ) : (
          <table style={{ ...styles.table, fontSize: 12 }}>
            <thead><tr>
              <th style={styles.th}>Account #</th>
              <th style={styles.th}>Inv #</th>
              <th style={styles.th}>Seq</th>
              <th style={styles.th}>Customer</th>
              <th style={styles.th}>Status</th>
              <th style={right}>Invoice ex-tax</th>
              <th style={right}>Lines</th>
              <th style={right}>Over by</th>
            </tr></thead>
            <tbody>
              {data.lines_exceed_invoice.slice(0, SHOWN).map(r => (
                <tr key={idKey(r.account_no, r.invoice_no, r.invoice_seq)}>
                  <td style={mono}>{r.account_no || '—'}</td>
                  <td style={mono}>{r.invoice_no}</td>
                  <td style={mono}>{r.invoice_seq}</td>
                  <td style={styles.td}>{r.customer_name || '—'}</td>
                  <td style={styles.td}>{r.invoice_status || '—'}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{money(r.invoice_ex_tax)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }} title={`${r.line_count} lines`}>{money(r.lines_total)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, color: '#991B1B' }}>{money(r.over_by)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function CommissionsPaymentsView({ year }) {
  const [payments, setPayments] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api.listCommissionPayments().then(setPayments).catch(e => setError(e.message));
  }, [year]);

  const remove = async (id) => {
    if (!confirm('Delete this commission payment record? Owed amount will go up by this payment.')) return;
    try { await api.deleteCommissionPayment(id); api.listCommissionPayments().then(setPayments); }
    catch (e) { setError(e.message); }
  };

  if (error) return <div style={styles.errorBox}>{error}</div>;
  if (!payments) return <div style={{ padding: 20, color: '#6B7280' }}>Loading…</div>;
  if (!payments.length) return (
    <div style={{ padding: 24, textAlign: 'center', color: '#6B7280', background: '#F9FAFB', borderRadius: 8 }}>
      No commission payments recorded yet. Use "+ Record Payment" on the Per-Rep Summary tab to add one.
    </div>
  );

  return (
    <div style={styles.card}>
      <table style={{ ...styles.table, fontSize: 13 }}>
        <thead><tr>
          <th style={styles.th}>Pay Period</th>
          <th style={styles.th}>Account Manager</th>
          <th style={{ ...styles.th, textAlign: 'right' }}>Amount Paid</th>
          <th style={styles.th}>Notes</th>
          <th style={styles.th}>Recorded</th>
          <th style={styles.th}></th>
        </tr></thead>
        <tbody>
          {payments.map(p => (
            <tr key={p.id}>
              <td style={styles.td}>{p.pay_period_start} → {p.pay_period_end}</td>
              <td style={{ ...styles.td, fontWeight: 600 }}>{p.user_name}</td>
              <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>${Math.round(p.amount_paid).toLocaleString()}</td>
              <td style={{ ...styles.td, fontSize: 12, color: '#6B7280' }}>{p.notes || '—'}</td>
              <td style={{ ...styles.td, fontSize: 11, color: '#6B7280' }}>
                {fmtDateTime(p.created_at)} by {p.created_by_name || '—'}
              </td>
              <td style={styles.td}>
                <button style={{ ...styles.btnDanger, fontSize: 11, padding: '4px 8px' }} onClick={() => remove(p.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecordPaymentModal({ rep, onClose, onSaved }) {
  // Default pay period: most recent bi-weekly window ending today
  const today = new Date();
  const twoWeeksAgo = new Date(today.getTime() - 14 * 86400000);
  const fmtIso = (d) => d.toISOString().slice(0, 10);
  const [periodStart, setPeriodStart] = useState(fmtIso(twoWeeksAgo));
  const [periodEnd, setPeriodEnd] = useState(fmtIso(today));
  const [amount, setAmount] = useState(rep.owed > 0 ? rep.owed.toFixed(2) : '');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setBusy(true); setError('');
    try {
      await api.createCommissionPayment({
        user_id: rep.user_id,
        pay_period_start: periodStart,
        pay_period_end: periodEnd,
        amount_paid: Number(amount),
        notes: notes || null
      });
      onSaved();
    } catch (err) { setError(err.message); setBusy(false); }
  };

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={{ ...modalCard, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>Record Commission Payment</h2>
          <button style={modalClose} onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize: 13, color: '#6B7280', marginTop: 0 }}>
          Recording payment for <strong>{rep.full_name}</strong>.
          {rep.owed > 0 && <span> Currently owed: <strong>${Math.round(rep.owed).toLocaleString()}</strong>.</span>}
        </p>
        {error && <div style={styles.errorBox}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={styles.label}>Pay Period Start</label>
            <input type="date" style={styles.input} value={periodStart} onChange={e => setPeriodStart(e.target.value)} />
          </div>
          <div>
            <label style={styles.label}>Pay Period End</label>
            <input type="date" style={styles.input} value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} />
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={styles.label}>Amount Paid ($)</label>
          <input type="number" step="0.01" min="0" style={styles.input} value={amount} onChange={e => setAmount(e.target.value)} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={styles.label}>Notes (optional)</label>
          <textarea style={{ ...styles.input, minHeight: 60 }} value={notes} onChange={e => setNotes(e.target.value)} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={styles.btnSecondary} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={styles.btn} onClick={save} disabled={busy || !amount}>{busy ? 'Saving…' : 'Save Payment'}</button>
        </div>
      </div>
    </div>
  );
}

// ---- Mail tab ----------------------------------------------------------

function MailTab() {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [digestExtraTo, setDigestExtraTo] = useState('');
  const [compEmails, setCompEmails] = useState('');
  const [compDays, setCompDays] = useState('30');
  const [compSaved, setCompSaved] = useState(false);
  const [smsTestTo, setSmsTestTo] = useState('');
  const [jrEmails, setJrEmails] = useState('');
  const [jrDays, setJrDays] = useState('7');
  const [jrSaved, setJrSaved] = useState(false);
  const [vpEmails, setVpEmails] = useState('');
  const [vpSaved, setVpSaved] = useState(false);
  const [uwEmails, setUwEmails] = useState('');
  const [uwSaved, setUwSaved] = useState(false);
  const [mdEmails, setMdEmails] = useState('');
  const [mdSaved, setMdSaved] = useState(false);
  const [djEmails, setDjEmails] = useState('');
  const [djSaved, setDjSaved] = useState(false);
  const [prEmails, setPrEmails] = useState('');
  const [prSaved, setPrSaved] = useState(false);
  const [drEmails, setDrEmails] = useState('');
  const [drSaved, setDrSaved] = useState(false);

  useEffect(() => {
    api.mailStatus().then(setStatus).catch(() => {});
    api.getSettings().then(s => {
      setSettings(s);
      setCompEmails(s.compliance_alert_emails || '');
      setCompDays(s.compliance_alert_days || '30');
      setJrEmails(s.job_readiness_alert_emails || '');
      setJrDays(s.job_readiness_alert_days || '7');
      setVpEmails(s.vp_scorecard_emails || '');
      setUwEmails(s.upcoming_work_emails || '');
      setMdEmails(s.mob_demob_forecast_emails || '');
      setDjEmails(s.daily_jobs_report_emails || '');
      setPrEmails(s.po_redirect_alert_emails || '');
      setDrEmails(s.disa_red_report_emails || '');
    }).catch(() => {});
  }, []);

  const saveDisaRed = async () => {
    setError(''); setDrSaved(false);
    const parts = drEmails.split(',').map(x => x.trim()).filter(Boolean);
    const bad = parts.filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setError(`These don't look like email addresses: ${bad.join(', ')}`); return; }
    try {
      await api.updateSetting('disa_red_report_emails', parts.join(','));
      setSettings(prev => ({ ...prev, disa_red_report_emails: parts.join(',') }));
      setDrSaved(true);
      setTimeout(() => setDrSaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  const saveVpScorecard = async () => {
    setError(''); setVpSaved(false);
    const parts = vpEmails.split(',').map(x => x.trim()).filter(Boolean);
    const bad = parts.filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setError(`These don't look like email addresses: ${bad.join(', ')}`); return; }
    try {
      await api.updateSetting('vp_scorecard_emails', parts.join(','));
      setSettings(prev => ({ ...prev, vp_scorecard_emails: parts.join(',') }));
      setVpSaved(true);
      setTimeout(() => setVpSaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  const saveUpcomingWork = async () => {
    setError(''); setUwSaved(false);
    const parts = uwEmails.split(',').map(x => x.trim()).filter(Boolean);
    const bad = parts.filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setError(`These don't look like email addresses: ${bad.join(', ')}`); return; }
    try {
      await api.updateSetting('upcoming_work_emails', parts.join(','));
      setSettings(prev => ({ ...prev, upcoming_work_emails: parts.join(',') }));
      setUwSaved(true);
      setTimeout(() => setUwSaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  const saveMobDemob = async () => {
    setError(''); setMdSaved(false);
    const parts = mdEmails.split(',').map(x => x.trim()).filter(Boolean);
    const bad = parts.filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setError(`These don't look like email addresses: ${bad.join(', ')}`); return; }
    try {
      await api.updateSetting('mob_demob_forecast_emails', parts.join(','));
      setSettings(prev => ({ ...prev, mob_demob_forecast_emails: parts.join(',') }));
      setMdSaved(true);
      setTimeout(() => setMdSaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  const saveDailyJobs = async () => {
    setError(''); setDjSaved(false);
    const parts = djEmails.split(',').map(x => x.trim()).filter(Boolean);
    const bad = parts.filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setError(`These don't look like email addresses: ${bad.join(', ')}`); return; }
    try {
      await api.updateSetting('daily_jobs_report_emails', parts.join(','));
      setSettings(prev => ({ ...prev, daily_jobs_report_emails: parts.join(',') }));
      setDjSaved(true);
      setTimeout(() => setDjSaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  const savePoRedirect = async () => {
    setError(''); setPrSaved(false);
    const parts = prEmails.split(',').map(x => x.trim()).filter(Boolean);
    const bad = parts.filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setError(`These don't look like email addresses: ${bad.join(', ')}`); return; }
    try {
      await api.updateSetting('po_redirect_alert_emails', parts.join(','));
      setSettings(prev => ({ ...prev, po_redirect_alert_emails: parts.join(',') }));
      setPrSaved(true);
      setTimeout(() => setPrSaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  const saveJobReadiness = async () => {
    setError(''); setJrSaved(false);
    const clean = (v) => v.split(',').map(x => x.trim()).filter(Boolean).join(',');
    const bad = jrEmails.split(',').map(x => x.trim()).filter(Boolean)
      .filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setError(`These don't look like email addresses: ${bad.join(', ')}`); return; }
    const d = Number(jrDays);
    if (!Number.isFinite(d) || d < 1 || d > 90) { setError('Readiness window must be a number between 1 and 90 days.'); return; }
    try {
      await api.updateSetting('job_readiness_alert_emails', clean(jrEmails));
      await api.updateSetting('job_readiness_alert_days', String(d));
      setSettings(prev => ({ ...prev, job_readiness_alert_emails: clean(jrEmails), job_readiness_alert_days: String(d) }));
      setJrSaved(true);
      setTimeout(() => setJrSaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  const saveCompliance = async () => {
    setError(''); setCompSaved(false);
    const clean = (v) => v.split(',').map(x => x.trim()).filter(Boolean).join(',');
    const bad = compEmails.split(',').map(x => x.trim()).filter(Boolean)
      .filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setError(`These don't look like email addresses: ${bad.join(', ')}`); return; }
    const d = Number(compDays);
    if (!Number.isFinite(d) || d < 1 || d > 365) { setError('Alert window must be a number between 1 and 365 days.'); return; }
    try {
      await api.updateSetting('compliance_alert_emails', clean(compEmails));
      await api.updateSetting('compliance_alert_days', String(d));
      setSettings(prev => ({ ...prev, compliance_alert_emails: clean(compEmails), compliance_alert_days: String(d) }));
      setCompSaved(true);
      setTimeout(() => setCompSaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  const run = async (kind, fn) => {
    setBusy(kind); setError(''); setResult(null);
    try { setResult({ kind, ...(await fn()) }); }
    catch (err) { setError(err.message); }
    finally { setBusy(''); }
  };

  const toggle = async (key) => {
    const newVal = settings?.[key] === 'true' ? 'false' : 'true';
    setSettings({ ...settings, [key]: newVal });
    try {
      await api.updateSetting(key, newVal);
    } catch (err) {
      setError(err.message);
      // revert on failure
      setSettings(prev => ({ ...prev, [key]: newVal === 'true' ? 'false' : 'true' }));
    }
  };

  const dailyOn = settings?.daily_reminders_enabled === 'true';
  const weeklyOn = settings?.weekly_reminders_enabled === 'true';
  const estimateEmailsOn = settings?.estimate_emails_enabled === 'true';
  const complianceOn = settings?.compliance_alert_enabled !== 'false';
  const jobReadinessOn = settings?.job_readiness_alert_enabled !== 'false';
  const complianceSmsOn = settings?.compliance_sms_enabled === 'true';
  const vpScorecardOn = settings?.vp_scorecard_enabled !== 'false';
  const upcomingWorkOn = settings?.upcoming_work_enabled !== 'false';
  const dailyJobsOn = settings?.daily_jobs_report_enabled !== 'false';
  const poRedirectOn = settings?.po_redirect_alert_enabled !== 'false';
  const mobDemobOn = settings?.mob_demob_forecast_enabled !== 'false';
  const disaRedOn = settings?.disa_red_report_enabled !== 'false';
  const smsConfigured = !!status?.sms_configured;
  const disabled = !status?.configured;

  return (
    <>
      <h2 style={styles.h2}>Email Notifications</h2>
      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.card}>
        <p style={{ marginTop: 0 }}>
          SMTP status: <strong>{status?.configured ? 'Configured' : 'Not configured'}</strong>
        </p>
        <p style={{ color: '#6B7280', fontSize: 13 }}>
          To enable email, set these env vars on the Railway service: <code>SMTP_HOST</code>, <code>SMTP_PORT</code>, <code>SMTP_USER</code>, <code>SMTP_PASS</code>, <code>SMTP_FROM</code>.
        </p>
      </div>

      <div style={styles.card}>
        <h2 style={styles.h2}>Test SMTP</h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          Sends one email to <strong>your</strong> address (the user you're logged in as) so you can confirm SMTP works without needing real call log data.
        </p>
        <button style={styles.btn} onClick={() => run('test', api.sendTestEmail)} disabled={disabled || !!busy}>
          {busy === 'test' ? 'Sending...' : 'Send Test Email to Me'}
        </button>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>Weekly Submission Reminder</h2>
          <ToggleRow on={weeklyOn} onChange={() => toggle('weekly_reminders_enabled')} disabled={!settings} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 13 }}>
          Runs automatically every <strong>Monday at 7:00 AM</strong>. Each rep with no entries for the current week gets an email; supervisors / leadership / admin get a digest of which of their reps are missing. {weeklyOn ? '' : <strong style={{ color: '#92400E' }}>Currently OFF — cron will skip even if 7am rolls around.</strong>}
        </p>
        <button style={styles.btn} onClick={() => run('weekly', api.sendWeekly)} disabled={disabled || !weeklyOn || !!busy}>
          {busy === 'weekly' ? 'Sending...' : 'Run Weekly Reminder Now'}
        </button>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>Daily Follow-up Reminder</h2>
          <ToggleRow on={dailyOn} onChange={() => toggle('daily_reminders_enabled')} disabled={!settings} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 13 }}>
          Runs automatically every day at <strong>7:00 AM</strong>. Each rep with open call-log entries whose <em>follow-up date</em> is today or overdue gets an email listing them. {dailyOn ? '' : <strong style={{ color: '#92400E' }}>Currently OFF — cron will skip even if 7am rolls around.</strong>}
        </p>
        <button style={styles.btn} onClick={() => run('daily', api.sendReminders)} disabled={disabled || !dailyOn || !!busy}>
          {busy === 'daily' ? 'Sending...' : 'Run Follow-up Reminder Now'}
        </button>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>📊 Estimate Emails</h2>
          <ToggleRow on={estimateEmailsOn} onChange={() => toggle('estimate_emails_enabled')} disabled={!settings} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 13 }}>
          The two automatic estimate emails: <strong>ready for approval</strong> (fires when an estimate is
          Submitted, to the estimate notify list) and the <strong>Approved / Rejected decision</strong> (to the AM).
          Manual sends — Email to Sales, vendor emails, CSVM proposal — are unaffected.
          {estimateEmailsOn ? '' : <strong style={{ color: '#92400E' }}> Currently OFF — submits and approval decisions send nothing (default while migrating legacy projects). Use the 📧 Send Email button on the estimate to send either one on demand.</strong>}
        </p>
      </div>

      <div style={styles.card}>
        <h2 style={styles.h2}>📋 Project Update Digest</h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          Sends the weekly project-update digest now. Ali Knox (<code>WEEKLY_UPDATE_DIGEST_TO</code>) receives
          the full digest with every AM listed. Each AM who posted an update this week receives their own section only.
          Use the field below to CC additional addresses on this one send.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <input
            style={{ ...styles.input, flex: '1 1 260px' }}
            placeholder="Extra CC (comma-separated, optional)"
            value={digestExtraTo}
            onChange={e => setDigestExtraTo(e.target.value)}
          />
          <button style={styles.btn} onClick={() => run('digest', () => api.sendDigestNow(digestExtraTo))} disabled={disabled || !!busy}>
            {busy === 'digest' ? 'Sending...' : 'Send Digest Now'}
          </button>
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.h2}>🎂 Birthday Wishes</h2>
        <p style={{ color: '#6B7280', fontSize: 13 }}>
          Runs automatically every day at <strong>8:00 AM</strong> (in each rep's branch local time). Anyone whose Birthday (Admin → Users) matches today gets a Happy Birthday email. Year on the Birthday field is ignored — only month + day count.
        </p>
        <button style={styles.btn} onClick={() => run('birthdays', api.sendBirthdays)} disabled={disabled || !!busy}>
          {busy === 'birthdays' ? 'Sending...' : 'Run Birthday Wishes Now'}
        </button>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>🛡️ Compliance Expiration Alerts</h2>
          <ToggleRow on={complianceOn} onChange={() => toggle('compliance_alert_enabled')} disabled={!settings} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 13 }}>
          Runs automatically every day at <strong>7:00 AM Central</strong>. Scans every active employee's DISA
          (drug test + background check), certifications / Safety Council classes, TWIC, and stored documents,
          and emails <strong>one digest</strong> of everything expired or expiring within the window below.
          {complianceOn ? '' : <strong style={{ color: '#92400E' }}> Currently OFF — cron will skip even at 7am.</strong>}
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <input
            style={{ ...styles.input, flex: '1 1 320px' }}
            placeholder="Recipient emails, comma-separated (dispatch / compliance team)"
            value={compEmails}
            onChange={e => setCompEmails(e.target.value)}
          />
          <input
            type="number" min={1} max={365}
            style={{ ...styles.input, width: 90 }}
            value={compDays}
            onChange={e => setCompDays(e.target.value)}
          />
          <span style={{ color: '#6B7280', fontSize: 13 }}>days ahead</span>
          <button style={styles.btn} onClick={saveCompliance}>Save</button>
          {compSaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
        <p style={{ color: '#9CA3AF', fontSize: 12, marginTop: 0 }}>
          With no recipients set, the daily email is skipped. <strong>Preview</strong> shows the counts without
          emailing; <strong>Run Now</strong> sends the digest immediately.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            style={{ ...styles.btn, background: '#fff', color: '#1F2937', border: '1px solid #D1D5DB' }}
            onClick={() => run('compliance-preview', api.previewComplianceAlerts)}
            disabled={!!busy}
          >
            {busy === 'compliance-preview' ? 'Checking...' : 'Preview (no email)'}
          </button>
          <button style={styles.btn} onClick={() => run('compliance', api.sendComplianceAlerts)} disabled={disabled || !complianceOn || !!busy}>
            {busy === 'compliance' ? 'Sending...' : 'Run Compliance Digest Now'}
          </button>
        </div>

        <div style={{ borderTop: '1px solid #E5E7EB', marginTop: 16, paddingTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>📱 Also text employees (Twilio) — optional</h3>
            <ToggleRow on={complianceSmsOn} onChange={() => toggle('compliance_sms_enabled')} disabled={!settings} />
          </div>
          <p style={{ color: '#6B7280', fontSize: 13 }}>
            When on, the same daily scan texts each employee whose certification / DISA / TWIC is expiring,
            telling them to contact their manager. Texts go <strong>only</strong> to employees who have a
            <strong> Mobile</strong> on file and <strong>SMS opt-in</strong> checked (Operations → Roster → Edit).
            Cadence: one text at 30 / 14 / 7 / 1 days out and on the expiration date. Every message ends with
            “Reply STOP to opt out.” Twilio: <strong>{smsConfigured ? 'Configured' : 'Not configured'}</strong>.
            {smsConfigured ? '' : <span style={{ color: '#92400E' }}> Set <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, and <code>TWILIO_MESSAGING_SERVICE_SID</code> (or <code>TWILIO_FROM</code>) on Railway.</span>}
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            <input
              style={{ ...styles.input, flex: '1 1 220px' }}
              placeholder="Test number, e.g. (281) 555-0143"
              value={smsTestTo}
              onChange={e => setSmsTestTo(e.target.value)}
            />
            <button
              style={{ ...styles.btn, background: '#fff', color: '#1F2937', border: '1px solid #D1D5DB' }}
              onClick={() => run('sms-test', () => api.sendSmsTest(smsTestTo))}
              disabled={!smsConfigured || !smsTestTo.trim() || !!busy}
            >
              {busy === 'sms-test' ? 'Texting...' : 'Send Test Text'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              style={{ ...styles.btn, background: '#fff', color: '#1F2937', border: '1px solid #D1D5DB' }}
              onClick={() => run('sms-preview', api.previewComplianceSms)}
              disabled={!!busy}
            >
              {busy === 'sms-preview' ? 'Checking...' : 'Preview Texts (no send)'}
            </button>
            <button style={styles.btn} onClick={() => run('sms', api.sendComplianceSms)} disabled={!smsConfigured || !complianceSmsOn || !!busy}>
              {busy === 'sms' ? 'Texting...' : 'Run Compliance Texts Now'}
            </button>
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>🚦 Job Readiness Digest</h2>
          <ToggleRow on={jobReadinessOn} onChange={() => toggle('job_readiness_alert_enabled')} disabled={!settings} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 13 }}>
          Runs automatically every day at <strong>7:00 AM Central</strong>. Scans every job with a job number
          starting within the window (plus live jobs) and emails <strong>one digest</strong> of everything not
          ready: no crew, a TBD placeholder still holding a seat, a staffing shortfall vs the plan, Pending
          verification boxes, no customer linked, or no site requirements on file for that customer.
          Jobs that are fully ready are left out.
          {jobReadinessOn ? '' : <strong style={{ color: '#92400E' }}> Currently OFF — cron will skip even at 7am.</strong>}
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <input
            style={{ ...styles.input, flex: '1 1 320px' }}
            placeholder="Recipient emails, comma-separated (ops / dispatch team)"
            value={jrEmails}
            onChange={e => setJrEmails(e.target.value)}
          />
          <input
            type="number" min={1} max={90}
            style={{ ...styles.input, width: 90 }}
            value={jrDays}
            onChange={e => setJrDays(e.target.value)}
          />
          <span style={{ color: '#6B7280', fontSize: 13 }}>days ahead</span>
          <button style={styles.btn} onClick={saveJobReadiness}>Save</button>
          {jrSaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
        <p style={{ color: '#9CA3AF', fontSize: 12, marginTop: 0 }}>
          With no recipients set, the daily email is skipped. <strong>Preview</strong> shows the counts without
          emailing; <strong>Run Now</strong> sends the digest immediately.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            style={{ ...styles.btn, background: '#fff', color: '#1F2937', border: '1px solid #D1D5DB' }}
            onClick={() => run('readiness-preview', api.previewJobReadiness)}
            disabled={!!busy}
          >
            {busy === 'readiness-preview' ? 'Checking...' : 'Preview (no email)'}
          </button>
          <button style={styles.btn} onClick={() => run('readiness', api.sendJobReadiness)} disabled={disabled || !jobReadinessOn || !!busy}>
            {busy === 'readiness' ? 'Sending...' : 'Run Readiness Digest Now'}
          </button>
        </div>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>🛡 DISA Red Status (weekly)</h2>
          <ToggleRow on={disaRedOn} onChange={() => toggle('disa_red_report_enabled')} disabled={!settings} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 13 }}>
          Runs automatically every <strong>Monday at 7:00 AM Central</strong>. One rollup email of every active
          employee showing <strong style={{ color: '#EF4444' }}>red</strong> on the DISA tab — background check
          expired or expiring within 30 days — with expiry date, days left, and last test.
          Nothing red = no email.
          {disaRedOn ? '' : <strong style={{ color: '#92400E' }}> Currently OFF — cron will skip even at 7am Monday.</strong>}
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <input
            style={{ ...styles.input, flex: '1 1 320px' }}
            placeholder="Recipient emails, comma-separated (dispatch)"
            value={drEmails}
            onChange={e => setDrEmails(e.target.value)}
          />
          <button style={styles.btn} onClick={saveDisaRed}>Save</button>
          {drSaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            style={{ ...styles.btn, background: '#fff', color: '#1F2937', border: '1px solid #D1D5DB' }}
            onClick={() => run('disa-red-preview', api.previewDisaRed)}
            disabled={!!busy}
          >
            {busy === 'disa-red-preview' ? 'Checking...' : 'Preview (no email)'}
          </button>
          <button style={styles.btn} onClick={() => run('disa-red', api.sendDisaRed)} disabled={disabled || !disaRedOn || !!busy}>
            {busy === 'disa-red' ? 'Sending...' : 'Run DISA Red Report Now'}
          </button>
        </div>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>🎯 VP Sales Scorecard</h2>
          <ToggleRow on={vpScorecardOn} onChange={() => toggle('vp_scorecard_enabled')} disabled={!settings} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 13 }}>
          Runs automatically every <strong>Monday at 7:00 AM Central</strong>. One email with a per-Account-Manager
          table for the week that just ended: activity logged (with week-over-week), pipeline + stuck bids, deals
          moved to Won / Lost in the last 7 days (with loss reasons), overdue follow-ups, and realized Rentalman
          revenue vs the Performance Target ladder with a pace column. Sales-side only.
          {vpScorecardOn ? '' : <strong style={{ color: '#92400E' }}> Currently OFF — cron will skip even at 7am Monday.</strong>}
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <input
            style={{ ...styles.input, flex: '1 1 320px' }}
            placeholder="Recipient emails, comma-separated (VP of Sales)"
            value={vpEmails}
            onChange={e => setVpEmails(e.target.value)}
          />
          <button style={styles.btn} onClick={saveVpScorecard}>Save</button>
          {vpSaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            style={{ ...styles.btn, background: '#fff', color: '#1F2937', border: '1px solid #D1D5DB' }}
            onClick={() => run('vp-preview', () => api.sendVpScorecard(true))}
            disabled={!!busy}
          >
            {busy === 'vp-preview' ? 'Checking...' : 'Preview (no email)'}
          </button>
          <button style={styles.btn} onClick={() => run('vp', () => api.sendVpScorecard(false))} disabled={disabled || !vpScorecardOn || !!busy}>
            {busy === 'vp' ? 'Sending...' : 'Send Scorecard Now'}
          </button>
        </div>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>📅 Upcoming Won Work</h2>
          <ToggleRow on={upcomingWorkOn} onChange={() => toggle('upcoming_work_enabled')} disabled={!settings} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 13 }}>
          Runs automatically every <strong>Monday at 6:00 AM Central</strong>. One email, three sections: jobs Won but
          not started and open jobs at 100% (dates, scope, planned manpower by craft, peak equipment with its on-site
          dates from the approved estimate(s)), and jobs running now (last day, the Field Services onsite crew, and the
          RentalMan units on the job's TA sub-branch). Turnarounds only: branch-billed jobs are left out. Bill side only.
          {upcomingWorkOn ? '' : <strong style={{ color: '#92400E' }}> Currently OFF — cron will skip even at 6am Monday.</strong>}
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <input
            style={{ ...styles.input, flex: '1 1 320px' }}
            placeholder="Recipient emails, comma-separated"
            value={uwEmails}
            onChange={e => setUwEmails(e.target.value)}
          />
          <button style={styles.btn} onClick={saveUpcomingWork}>Save</button>
          {uwSaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            style={{ ...styles.btn, background: '#fff', color: '#1F2937', border: '1px solid #D1D5DB' }}
            onClick={() => run('uw-preview', () => api.sendUpcomingWork(true))}
            disabled={!!busy}
          >
            {busy === 'uw-preview' ? 'Checking...' : 'Preview (no email)'}
          </button>
          <button style={styles.btn} onClick={() => run('uw', () => api.sendUpcomingWork(false))} disabled={disabled || !upcomingWorkOn || !!busy}>
            {busy === 'uw' ? 'Sending...' : 'Send Upcoming Work Now'}
          </button>
        </div>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>💵 Mob / Demob Cash Forecast</h2>
          <ToggleRow on={mobDemobOn} onChange={() => toggle('mob_demob_forecast_enabled')} disabled={!settings} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 13 }}>
          Runs automatically every <strong>Monday at 6:00 AM Central</strong>. What SSS will pay out over the next six weeks,
          week by week, for crews and equipment moving onto and off jobs: crew mileage, freight and rental vehicles, plus
          what moved since last Monday. Everyone is costed at mileage. Close-outs read the Field Services onsite roster (each
          person's last day, the trip home at what we billed for the trip in) and price freight back at what we billed
          inbound; start-ups read the approved estimate. <strong>Internal</strong>: send it to accounting only. Turnarounds only.
          {mobDemobOn ? '' : <strong style={{ color: '#92400E' }}> Currently OFF — cron will skip even at 6am Monday.</strong>}
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <input
            style={{ ...styles.input, flex: '1 1 320px' }}
            placeholder="Recipient emails, comma-separated"
            value={mdEmails}
            onChange={e => setMdEmails(e.target.value)}
          />
          <button style={styles.btn} onClick={saveMobDemob}>Save</button>
          {mdSaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            style={{ ...styles.btn, background: '#fff', color: '#1F2937', border: '1px solid #D1D5DB' }}
            onClick={() => run('md-preview', () => api.sendMobDemob(true))}
            disabled={!!busy}
          >
            {busy === 'md-preview' ? 'Checking...' : 'Preview (no email)'}
          </button>
          <button style={styles.btn} onClick={() => run('md', () => api.sendMobDemob(false))} disabled={disabled || !mobDemobOn || !!busy}>
            {busy === 'md' ? 'Sending...' : 'Send Cash Forecast Now'}
          </button>
        </div>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>🗓 Daily Jobs Report</h2>
          <ToggleRow on={dailyJobsOn} onChange={() => toggle('daily_jobs_report_enabled')} disabled={!settings} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 13 }}>
          Runs automatically <strong>Monday through Friday at 7:00 AM Central</strong>. Every Active job, every Won job
          starting in the next 60 days and every job completed in the last 30 days, each with job #, start / end, PO #, branch,
          account manager and personnel (Field Services roster wins, else the dispatched crew). Hidden jobs are
          left out. No dollars.
          {dailyJobsOn ? '' : <strong style={{ color: '#92400E' }}> Currently OFF — cron will skip.</strong>}
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <input
            style={{ ...styles.input, flex: '1 1 320px' }}
            placeholder="Recipient emails, comma-separated"
            value={djEmails}
            onChange={e => setDjEmails(e.target.value)}
          />
          <button style={styles.btn} onClick={saveDailyJobs}>Save</button>
          {djSaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            style={{ ...styles.btn, background: '#fff', color: '#1F2937', border: '1px solid #D1D5DB' }}
            onClick={() => run('dj-preview', () => api.sendDailyJobs(true))}
            disabled={!!busy}
          >
            {busy === 'dj-preview' ? 'Checking...' : 'Preview (no email)'}
          </button>
          <button style={styles.btn} onClick={() => run('dj', () => api.sendDailyJobs(false))} disabled={disabled || !dailyJobsOn || !!busy}>
            {busy === 'dj' ? 'Sending...' : 'Send Daily Jobs Now'}
          </button>
        </div>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>🔀 TA PO Billed at a Branch</h2>
          <ToggleRow on={poRedirectOn} onChange={() => toggle('po_redirect_alert_enabled')} disabled={!settings} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 13 }}>
          Checked <strong>every 30 minutes</strong> with the RentalMan invoices sync. Emails when a turnaround opportunity's
          PO (job # 800-899, or revenue type TA) is invoiced out of a RentalMan branch outside 08xx. Each invoice is
          reported once. Switching the job TA &rarr; BR on Active Jobs stops it. The CRM never changes the opportunity itself.
          {poRedirectOn ? '' : <strong style={{ color: '#92400E' }}> Currently OFF — no alerts.</strong>}
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <input
            style={{ ...styles.input, flex: '1 1 320px' }}
            placeholder="Recipient emails, comma-separated"
            value={prEmails}
            onChange={e => setPrEmails(e.target.value)}
          />
          <button style={styles.btn} onClick={savePoRedirect}>Save</button>
          {prSaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            style={{ ...styles.btn, background: '#fff', color: '#1F2937', border: '1px solid #D1D5DB' }}
            onClick={() => run('pr-preview', () => api.sendPoRedirect(true))}
            disabled={!!busy}
          >
            {busy === 'pr-preview' ? 'Checking...' : 'Preview (no email)'}
          </button>
          <button style={styles.btn} onClick={() => run('pr', () => api.sendPoRedirect(false))} disabled={disabled || !poRedirectOn || !!busy}>
            {busy === 'pr' ? 'Sending...' : 'Send New Alerts Now'}
          </button>
        </div>
      </div>

      {result && (
        <div style={{ ...styles.card, background: '#ECFDF5', borderColor: '#10B981' }}>
          <h2 style={{ ...styles.h2, marginTop: 0 }}>Last Run</h2>
          <pre style={{ fontSize: 12, margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </>
  );
}

// ---- Cleanup tab -------------------------------------------------------
// Surfaces data integrity issues admin can fix in one click. First (and
// currently only) tool: duplicate Activity Log entries -- accidental
// re-uploads are the usual cause. Each group's row keeps the OLDEST id
// (smallest -- created first) and offers to delete the rest.
function CleanupTab() {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [justCleaned, setJustCleaned] = useState('');

  const reload = () => {
    setError(''); setJustCleaned('');
    api.findCallLogDuplicates().then(setGroups).catch(e => setError(e.message));
  };
  useEffect(() => { reload(); }, []);

  // For a single group: keep oldest, delete the rest.
  const cleanGroup = async (group) => {
    if (!confirm(`Delete ${group.dup_count - 1} duplicate entr${group.dup_count - 1 === 1 ? 'y' : 'ies'} for ${group.rep_name} · ${group.customer}? Oldest entry kept.`)) return;
    const toDelete = group.ids.slice(1); // ids are oldest-first; keep [0]
    setBusy(true);
    try {
      const r = await api.cleanupCallLogDuplicates(toDelete);
      setJustCleaned(`Deleted ${r.deleted} duplicate${r.deleted === 1 ? '' : 's'}.`);
      reload();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  // Big-red-button: clean every group at once. Same "keep oldest" rule.
  const cleanAll = async () => {
    if (!groups || !groups.length) return;
    const total = groups.reduce((s, g) => s + (g.dup_count - 1), 0);
    if (!confirm(`Delete ${total} duplicate entries across ${groups.length} groups? Oldest entry in each group is kept. This cannot be undone.`)) return;
    const allDeleteIds = groups.flatMap(g => g.ids.slice(1));
    setBusy(true);
    try {
      const r = await api.cleanupCallLogDuplicates(allDeleteIds);
      setJustCleaned(`Deleted ${r.deleted} duplicate${r.deleted === 1 ? '' : 's'} across ${groups.length} groups.`);
      reload();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  if (groups === null) return <div>Loading...</div>;

  const totalDupes = groups.reduce((s, g) => s + (g.dup_count - 1), 0);

  return (
    <>
      <h2 style={styles.h2}>Cleanup Tools</h2>
      {error && <div style={styles.errorBox}>{error}</div>}
      {justCleaned && (
        <div style={{ background: '#D1FAE5', color: '#065F46', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          {justCleaned}
        </div>
      )}

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>
            Duplicate Activity Log Entries
            <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
              · {groups.length} group{groups.length === 1 ? '' : 's'} · {totalDupes} extra row{totalDupes === 1 ? '' : 's'}
            </span>
          </h2>
          {groups.length > 0 && (
            <button style={styles.btnDanger} onClick={cleanAll} disabled={busy}>
              {busy ? 'Cleaning…' : `Delete all ${totalDupes} duplicates (keep oldest)`}
            </button>
          )}
        </div>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          Groups by <strong>rep + week + customer + contact</strong>. Each group shows row IDs oldest-first; the cleanup keeps the oldest and deletes the rest.
          Most common cause: re-uploading a call log file.
        </p>

        {groups.length === 0 && (
          <div style={{ color: '#065F46', background: '#D1FAE5', padding: '12px', borderRadius: 6, fontSize: 13 }}>
            ✓ No duplicates. The Activity Log is clean.
          </div>
        )}

        {groups.length > 0 && (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Account Manager</th>
                <th style={styles.th}>Week Of</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Contact</th>
                <th style={styles.th}>Count</th>
                <th style={styles.th}>Row IDs</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g, i) => (
                <tr key={`${g.rep_id}-${g.week_of}-${i}`}>
                  <td style={styles.td}>{g.rep_name}</td>
                  <td style={styles.td}>{g.week_of ? new Date(g.week_of).toISOString().slice(0, 10) : ''}</td>
                  <td style={styles.td}><strong>{g.customer}</strong></td>
                  <td style={styles.td}>{g.contact_name || <span style={{ color: '#9CA3AF' }}>—</span>}</td>
                  <td style={styles.td}>{g.dup_count}</td>
                  <td style={{ ...styles.td, fontSize: 11, color: '#6B7280' }}>
                    keep <strong>{g.ids[0]}</strong> · delete {g.ids.slice(1).join(', ')}
                  </td>
                  <td style={styles.td}>
                    <button style={styles.btnDanger} onClick={() => cleanGroup(g)} disabled={busy}>
                      Delete {g.dup_count - 1}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ---- Backup tab --------------------------------------------------------
//
// Surfaces nightly Postgres backup status (last run, last error, size of
// most recent dump) and gives admin a "Run now" button + a list of the
// recent backups in R2. The nightly cron runs at 02:00 UTC; this tab is
// the human-friendly inspection point.

const fmtBytes = (n) => {
  const num = Number(n) || 0;
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / 1024 / 1024).toFixed(1)} MB`;
};

// DISA portal sync — the MinisForum task "SSS DISA Daily Pull" pushes the
// DISAWorks export to R2 once daily at 03:00; this card shows the last
// auto-import and lets admin force one now.
function DisaSyncCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = async () => {
    setError('');
    try { setStatus(await api.disaSyncStatus()); }
    catch (e) { setError(e.message); }
  };
  useEffect(() => { load(); }, []);

  const toggleEnabled = async () => {
    if (!status) return;
    setError(''); setInfo('');
    try {
      await api.updateSetting('disa_sync_enabled', status.enabled ? 'false' : 'true');
      await load();
    } catch (e) { setError(e.message); }
  };

  const runNow = async () => {
    if (!status?.configured) {
      setError('R2 not configured. Set BACKUP_R2_* env vars on Railway first.');
      return;
    }
    setBusy(true); setError(''); setInfo('');
    try {
      const r = await api.runDisaSync();
      setInfo(r.skipped
        ? `No change since last sync (${r.report_modified ? fmtDateTime(r.report_modified) : '—'}).`
        : `Imported ${r.rows_in_file} rows: ${r.employees_matched} matched, ${r.employees_added} added.`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <div style={styles.card}>
      <h2 style={styles.h2}>🛡 DISA Portal Sync</h2>
      <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
        The MinisForum task "SSS DISA Daily Pull" pulls the saved DISAWorks "Jorge" views once daily
        at 3 AM and drops them in R2. This imports them automatically through the same path as the
        manual Operations → DISA upload. Freshness shows on the Operations → DISA tab badge.
      </p>
      {error && <div style={styles.errorBox}>{error}</div>}
      {info && <div style={{ ...styles.errorBox, background: '#ECFDF5', borderColor: '#10B981', color: '#065F46' }}>{info}</div>}
      <table style={{ ...styles.table, marginBottom: 12 }}>
        <tbody>
          <tr><td><strong>R2 configured</strong></td><td>{status.configured ? '✓ yes' : '✗ no (set BACKUP_R2_* env vars)'}</td></tr>
          <tr><td><strong>R2 key</strong></td><td style={{ fontFamily: 'monospace', fontSize: 12 }}>{status.r2_key}</td></tr>
          <tr><td><strong>Auto-import enabled</strong></td>
            <td>
              {status.enabled ? '✓ on' : '✗ off'}{' '}
              <button onClick={toggleEnabled} style={{ ...styles.btnSecondary, padding: '2px 10px', fontSize: 12, marginLeft: 8 }}>
                Turn {status.enabled ? 'off' : 'on'}
              </button>
            </td>
          </tr>
          <tr><td><strong>Last run</strong></td><td>{status.last_run_at ? fmtDateTime(status.last_run_at) : 'never'}</td></tr>
          <tr><td><strong>Last status</strong></td>
            <td>
              {status.last_status === 'ok' && <span style={{ color: '#10B981' }}>✓ ok</span>}
              {status.last_status === 'skipped' && <span style={{ color: '#6B7280' }}>— unchanged</span>}
              {status.last_status === 'error' && <span style={{ color: '#EF4444' }}>✗ {status.last_error || 'error'}</span>}
              {!status.last_status && '—'}
            </td>
          </tr>
          <tr><td><strong>Last import</strong></td><td>{status.last_matched.toLocaleString()} matched · {status.last_added.toLocaleString()} added ({status.last_rows.toLocaleString()} rows)</td></tr>
          <tr><td><strong>Report file dated</strong></td><td>{status.report_modified ? fmtDateTime(status.report_modified) : '—'}</td></tr>
        </tbody>
      </table>
      <button onClick={runNow} disabled={busy} style={styles.btn}>
        {busy ? 'Syncing...' : 'Run DISA sync now'}
      </button>
      <button onClick={load} style={{ ...styles.btnSecondary, marginLeft: 8 }}>Refresh</button>
    </div>
  );
}

// ISNetworld per-employee training sync (2026-07-29). Same shape as the DISA
// card above — reads the ISN "Online Training Analytics → Export Details"
// workbook from the same R2 bucket the backups use.
function IsnSyncCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = async () => {
    setError('');
    try { setStatus(await api.isnSyncStatus()); }
    catch (e) { setError(e.message); }
  };
  useEffect(() => { load(); }, []);

  const toggleEnabled = async () => {
    if (!status) return;
    setError(''); setInfo('');
    try {
      await api.updateSetting('isn_sync_enabled', status.enabled ? 'false' : 'true');
      await load();
    } catch (e) { setError(e.message); }
  };

  const runNow = async () => {
    if (!status?.configured) {
      setError('R2 not configured. Set BACKUP_R2_* env vars on Railway first.');
      return;
    }
    setBusy(true); setError(''); setInfo('');
    try {
      const r = await api.runIsnSync();
      setInfo(r.skipped
        ? `No change since last sync (${r.report_modified ? fmtDateTime(r.report_modified) : '—'}).`
        : `Imported ${r.assignments} assignments: ${r.employees_matched} matched, ${r.employees_added} added, ${r.open_assignments} outstanding.`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <div style={styles.card}>
      <h2 style={styles.h2}>🌐 ISNetworld Training Sync</h2>
      <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
        Imports the ISN "Online Training Analytics → Export Details" workbook from R2 — every training a
        hiring client has assigned to an SSS worker, <strong>including the ones not yet taken</strong>.
        Same path as the manual Operations → ISNetworld → Worker Training upload, which works whether or
        not this sync is running.
      </p>
      {error && <div style={styles.errorBox}>{error}</div>}
      {info && <div style={{ ...styles.errorBox, background: '#ECFDF5', borderColor: '#10B981', color: '#065F46' }}>{info}</div>}
      <table style={{ ...styles.table, marginBottom: 12 }}>
        <tbody>
          <tr><td><strong>R2 configured</strong></td><td>{status.configured ? '✓ yes' : '✗ no (set BACKUP_R2_* env vars)'}</td></tr>
          <tr><td><strong>R2 key</strong></td><td style={{ fontFamily: 'monospace', fontSize: 12 }}>{status.r2_key}</td></tr>
          <tr><td><strong>Auto-import enabled</strong></td>
            <td>
              {status.enabled ? '✓ on' : '✗ off'}{' '}
              <button onClick={toggleEnabled} style={{ ...styles.btnSecondary, padding: '2px 10px', fontSize: 12, marginLeft: 8 }}>
                Turn {status.enabled ? 'off' : 'on'}
              </button>
            </td>
          </tr>
          <tr><td><strong>Last run</strong></td><td>{status.last_run_at ? fmtDateTime(status.last_run_at) : 'never'}</td></tr>
          <tr><td><strong>Last status</strong></td>
            <td>
              {status.last_status === 'ok' && <span style={{ color: '#10B981' }}>✓ ok</span>}
              {status.last_status === 'skipped' && <span style={{ color: '#6B7280' }}>— unchanged</span>}
              {status.last_status === 'error' && <span style={{ color: '#EF4444' }}>✗ {status.last_error || 'error'}</span>}
              {!status.last_status && '—'}
            </td>
          </tr>
          <tr><td><strong>Last import</strong></td><td>{status.last_matched.toLocaleString()} matched · {status.last_added.toLocaleString()} added ({status.last_rows.toLocaleString()} assignments)</td></tr>
          <tr><td><strong>Outstanding</strong></td>
            <td style={{ color: status.last_open ? '#EF4444' : '#10B981', fontWeight: 600 }}>
              {status.last_open ? `${status.last_open.toLocaleString()} not passed` : '—'}
            </td>
          </tr>
          <tr><td><strong>Export file dated</strong></td><td>{status.report_modified ? fmtDateTime(status.report_modified) : '—'}</td></tr>
        </tbody>
      </table>
      <button onClick={runNow} disabled={busy} style={styles.btn}>
        {busy ? 'Syncing...' : 'Run ISN sync now'}
      </button>
      <button onClick={load} style={{ ...styles.btnSecondary, marginLeft: 8 }}>Refresh</button>
    </div>
  );
}

// RentalMan "All Invoices" sync (George 2026-09-14, docs/SPEC-rm-invoiced-actuals.md
// section 6). Same shape as the DISA / ISN cards above. Pulled and Loaded are
// shown apart on purpose: a dead scraper freezes Pulled while Loaded keeps
// advancing on every unchanged-file check.
function RmInvoiceSyncCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const num = (v) => Number(v || 0).toLocaleString();

  const load = async () => {
    setError('');
    try { setStatus(await api.rmInvoiceSyncStatus()); }
    catch (e) { setError(e.message); }
  };
  useEffect(() => { load(); }, []);

  const toggleEnabled = async () => {
    if (!status) return;
    setError(''); setInfo('');
    try {
      await api.updateSetting('rm_invoice_sync_enabled', status.enabled ? 'false' : 'true');
      await load();
    } catch (e) { setError(e.message); }
  };

  const runNow = async () => {
    if (!status?.configured) {
      setError('R2 not configured. Set BACKUP_R2_* env vars on Railway first.');
      return;
    }
    setBusy(true); setError(''); setInfo('');
    try {
      const r = await api.runRmInvoiceSync();
      const c = r.counts || {};
      const a = c.actuals || {};
      setInfo(r.skipped
        ? `No change since last sync (${r.report_modified ? fmtDateTime(r.report_modified) : 'not dated'}).`
        : `Loaded ${num(c.rows_in_csv)} rows: ${num(c.inserted)} new, ${num(c.updated)} updated. Actual $: ${num(a.updated)} opportunities updated, ${num(a.cleared)} cleared, ${num(a.takeovers)} typed figures replaced.`);
      await load();
    } catch (e) {
      // Reload FIRST: load() clears the error banner, so setting it before the
      // reload erased a failure the status table never recorded.
      const msg = e.message;
      await load();
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return error ? <div style={styles.card}><h2 style={styles.h2}>RentalMan Invoices Sync</h2><div style={styles.errorBox}>{error}</div></div> : null;

  const counts = status.counts || {};
  const actuals = counts.actuals || null;
  // One chip, most serious state first. A skipped run on an old report is
  // stale, not fine.
  const chip = !status.configured ? { label: 'not configured', bg: '#F3F4F6', fg: '#4B5563' }
    : status.last_status === 'error' ? { label: 'failed', bg: '#FEE2E2', fg: '#991B1B' }
    : status.stale ? { label: 'stale', bg: '#FEF3C7', fg: '#92400E' }
    : status.last_status === 'skipped' ? { label: 'skipped', bg: '#E0E7FF', fg: '#3730A3' }
    : status.last_status === 'ok' ? { label: 'current', bg: '#D1FAE5', fg: '#065F46' }
    : { label: 'not run yet', bg: '#F3F4F6', fg: '#4B5563' };
  const chipTitle = {
    'not configured': 'R2 is not configured, so there is no feed to read.',
    failed: 'The last run failed. See Last error.',
    stale: `The last loaded report is older than the latest scheduled pull${status.pull_schedule ? ` (${status.pull_schedule})` : ''}, or none has loaded yet. The MinisForum pull may have missed a run.`,
    skipped: 'The report in R2 has not changed since it was last loaded. Nothing to do.',
    current: 'The latest report is loaded and every RentalMan Actual $ is recomputed.',
    'not run yet': 'No sync has run since this setting was created.',
  }[chip.label];

  return (
    <div style={styles.card}>
      <h2 style={styles.h2}>🧾 RentalMan Invoices Sync</h2>
      <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
        The MinisForum pushes the RentalMan All Invoices report to R2 three times a day. This loads it
        every 30 minutes when the file changes, then sets each Won / Active / Complete opportunity's
        Actual $ to the ex-tax total of the invoices billed to its PO. Admin → Commissions reads each
        invoice's paid status (Earned) from it; Realized $ does not.
      </p>
      {error && <div style={styles.errorBox}>{error}</div>}
      {info && <div style={{ ...styles.errorBox, background: '#ECFDF5', borderColor: '#10B981', color: '#065F46' }}>{info}</div>}
      <table style={{ ...styles.table, marginBottom: 12 }}>
        <tbody>
          <tr><td><strong>Status</strong></td>
            <td>
              <span title={chipTitle} style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.5, background: chip.bg, color: chip.fg }}>
                {chip.label}
              </span>
            </td>
          </tr>
          <tr><td><strong>R2 configured</strong></td><td>{status.configured ? '✓ yes' : '✗ no (set BACKUP_R2_* env vars)'}</td></tr>
          <tr><td><strong>R2 key</strong></td><td style={{ fontFamily: 'monospace', fontSize: 12 }}>{status.key}</td></tr>
          <tr><td><strong>Auto-sync enabled</strong></td>
            <td>
              {status.enabled ? '✓ on' : '✗ off'}{' '}
              <button onClick={toggleEnabled} style={{ ...styles.btnSecondary, padding: '2px 10px', fontSize: 12, marginLeft: 8 }}>
                Turn {status.enabled ? 'off' : 'on'}
              </button>
            </td>
          </tr>
          <tr><td><strong>Pulled</strong></td>
            <td title="When the MinisForum pushed the report file to R2.">{status.report_modified ? fmtDateTime(status.report_modified) : 'never'}</td>
          </tr>
          <tr><td><strong>Loaded</strong></td>
            <td title="When CRM last held the latest report (loaded it, or confirmed it unchanged).">{status.last_ok_at ? fmtDateTime(status.last_ok_at) : 'never'}</td>
          </tr>
          <tr><td><strong>Last run</strong></td>
            <td>
              {status.last_run_at ? fmtDateTime(status.last_run_at) : 'never'}
              {status.last_duration_ms ? ` (${(status.last_duration_ms / 1000).toFixed(1)}s)` : ''}
            </td>
          </tr>
          {status.last_status === 'error' && (
            <tr><td><strong>Last error</strong></td><td style={{ color: '#EF4444' }}>{status.last_error || 'error'}</td></tr>
          )}
          <tr><td><strong>Last load</strong></td>
            <td>
              {status.counts
                ? `${num(counts.rows_in_csv)} rows in file · ${num(counts.inserted)} inserted · ${num(counts.updated)} updated · ${num(counts.unchanged)} unchanged · ${num(counts.skipped)} skipped · ${num(counts.duplicates)} duplicates · ${num(counts.conflict_count ?? (Array.isArray(counts.conflicts) ? counts.conflicts.length : counts.conflicts))} conflicts`
                : 'no load yet'}
            </td>
          </tr>
          <tr><td><strong>Invoices on file</strong></td><td>{num(status.invoices_cached)}</td></tr>
          <tr><td><strong>Actual $</strong></td>
            <td>
              {actuals
                ? `${num(actuals.in_scope)} in scope · ${num(actuals.with_invoices)} with invoices · ${num(actuals.updated)} updated · ${num(actuals.cleared)} cleared · ${num(actuals.takeovers)} typed figures replaced`
                : (status.counts ? 'recompute did not complete on the last load' : 'no load yet')}
            </td>
          </tr>
        </tbody>
      </table>
      <button onClick={runNow} disabled={busy} style={styles.btn}>
        {busy ? 'Syncing...' : 'Run RentalMan invoices sync now'}
      </button>
      <button onClick={load} style={{ ...styles.btnSecondary, marginLeft: 8 }}>Refresh</button>
    </div>
  );
}

function HascSyncCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [backfillStart, setBackfillStart] = useState('');
  const [backfillEnd, setBackfillEnd] = useState('');

  const load = async () => {
    setError('');
    try { setStatus(await api.hascSyncStatus()); }
    catch (e) { setError(e.message); }
  };
  useEffect(() => { load(); }, []);

  const toggleEnabled = async () => {
    if (!status) return;
    setError(''); setInfo('');
    try {
      await api.updateSetting('hasc_sync_enabled', status.enabled ? 'false' : 'true');
      await load();
    } catch (e) { setError(e.message); }
  };

  const runNow = async () => {
    if (!status?.configured) {
      setError('No council API key set. Add HASC_API_KEY / HASC_API_KEY_CSCCB on Railway once HASC issues credentials.');
      return;
    }
    setBusy(true); setError(''); setInfo('');
    try {
      const r = await api.runHascSync(backfillStart, backfillEnd);
      setInfo((r.accounts || []).map(a => a.ok
        ? `${a.account}: ${a.records} records (${a.window.start} → ${a.window.end}), ${a.certs_inserted} certs added / ${a.certs_updated} refreshed`
        : `${a.account}: FAILED — ${a.error}`
      ).join('  ·  ') + ` — ${r.employees_added} added to roster${r.skipped_not_passed ? `, ${r.skipped_not_passed} non-pass rows skipped` : ''}.`);
      setBackfillStart(''); setBackfillEnd('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <div style={styles.card}>
      <h2 style={styles.h2}>🎓 HASC Training Sync</h2>
      <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
        Pulls training history from the HASC LINK API nightly at 8 PM Central and upserts it into
        employee training (same course-code matching the badging and Site Ready checks use). The API
        only returns courses registered under the SSS account — prior-employer history still comes
        from employee document uploads. HASC asks that multi-year backfills run after 6 PM Central.
      </p>
      {error && <div style={styles.errorBox}>{error}</div>}
      {info && <div style={{ ...styles.errorBox, background: '#ECFDF5', borderColor: '#10B981', color: '#065F46' }}>{info}</div>}
      <table style={{ ...styles.table, marginBottom: 12 }}>
        <tbody>
          <tr><td><strong>Council accounts</strong></td>
            <td>{(status.accounts || []).map(a => a.configured ? `${a.label} ✓` : `${a.label} ✗ (set ${a.key_env})`).join('  ·  ') || '✗ none configured'}</td>
          </tr>
          <tr><td><strong>Nightly sync enabled</strong></td>
            <td>
              {status.enabled ? '✓ on' : '✗ off'}{' '}
              <button onClick={toggleEnabled} style={{ ...styles.btnSecondary, padding: '2px 10px', fontSize: 12, marginLeft: 8 }}>
                Turn {status.enabled ? 'off' : 'on'}
              </button>
            </td>
          </tr>
          <tr><td><strong>Synced through</strong></td>
            <td>{(status.accounts || []).filter(a => a.configured || a.synced_through).map(a => `${a.label}: ${a.synced_through || 'never'}`).join('  ·  ') || 'never'}</td>
          </tr>
          <tr><td><strong>Last run</strong></td><td>{status.last_run_at ? fmtDateTime(status.last_run_at) : 'never'}</td></tr>
          <tr><td><strong>Last status</strong></td>
            <td>
              {status.last_status === 'ok' && <span style={{ color: '#10B981' }}>✓ ok</span>}
              {status.last_status === 'error' && <span style={{ color: '#EF4444' }}>✗ {status.last_error || 'error'}</span>}
              {!status.last_status && '—'}
            </td>
          </tr>
          {status.last_status === 'ok' && (
            <tr><td><strong>Last pull</strong></td>
              <td>
                {status.last_records.toLocaleString()} records · {status.last_people.toLocaleString()} people
                ({status.last_matched_ssn} SSN / {status.last_matched_name} name / {status.last_added} added)
                {status.last_certs ? ` · certs ${status.last_certs}` : ''}
                {status.last_window ? ` · ${status.last_window}` : ''}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={runNow} disabled={busy} style={styles.btn}>
          {busy ? 'Pulling...' : 'Run HASC sync now'}
        </button>
        <button onClick={load} style={{ ...styles.btnSecondary }}>Refresh</button>
        <span style={{ color: '#6B7280', fontSize: 12, marginLeft: 8 }}>Backfill range (optional):</span>
        <input type="date" value={backfillStart} onChange={e => setBackfillStart(e.target.value)} style={{ ...styles.input, width: 150 }} />
        <span style={{ color: '#6B7280', fontSize: 12 }}>to</span>
        <input type="date" value={backfillEnd} onChange={e => setBackfillEnd(e.target.value)} style={{ ...styles.input, width: 150 }} />
      </div>
      {/* HASC Phase 2: the pilot switch, the $0 check, the newest registrations. */}
      <HascRegistrationAdmin />
    </div>
  );
}

function BackupTab() {
  const [status, setStatus] = useState(null);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = async () => {
    setError('');
    try {
      const [s, l] = await Promise.all([api.backupStatus(), api.listBackups(30)]);
      setStatus(s);
      setList(l);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleEnabled = async () => {
    if (!status) return;
    setError(''); setInfo('');
    try {
      await api.updateSetting('backup_enabled', status.enabled ? 'false' : 'true');
      await load();
    } catch (e) { setError(e.message); }
  };

  const runNow = async () => {
    if (!status?.configured) {
      setError('R2 not configured. Set BACKUP_R2_* env vars on Railway first.');
      return;
    }
    setBusy(true); setError(''); setInfo('');
    try {
      const r = await api.runBackup();
      setInfo(`Backup uploaded: ${r.key} (${fmtBytes(r.size_bytes)}, ${r.rows} rows, pruned ${r.retention_deleted} old)`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <div>Loading...</div>;

  return (
    <>
      <h2 style={styles.h2}>Nightly Backup</h2>
      {error && <div style={styles.errorBox}>{error}</div>}
      {info && <div style={{ ...styles.errorBox, background: '#ECFDF5', borderColor: '#10B981', color: '#065F46' }}>{info}</div>}

      <div style={styles.card}>
        <h2 style={styles.h2}>Status</h2>
        <table style={{ ...styles.table, marginBottom: 12 }}>
          <tbody>
            <tr><td><strong>R2 configured</strong></td><td>{status.configured ? '✓ yes' : '✗ no (set BACKUP_R2_* env vars)'}</td></tr>
            <tr><td><strong>Bucket</strong></td><td>{status.bucket || '—'}</td></tr>
            <tr><td><strong>Schedule enabled</strong></td>
              <td>
                {status.enabled ? '✓ on' : '✗ off'}{' '}
                <button onClick={toggleEnabled} style={{ ...styles.btnSecondary, padding: '2px 10px', fontSize: 12, marginLeft: 8 }}>
                  Turn {status.enabled ? 'off' : 'on'}
                </button>
              </td>
            </tr>
            <tr><td><strong>Retention</strong></td><td>{status.retention_days} days</td></tr>
            <tr><td><strong>Last run</strong></td><td>{status.last_run_at ? fmtDateTime(status.last_run_at) : 'never'}</td></tr>
            <tr><td><strong>Last status</strong></td>
              <td>
                {status.last_status === 'ok' && <span style={{ color: '#10B981' }}>✓ ok</span>}
                {status.last_status === 'error' && <span style={{ color: '#EF4444' }}>✗ {status.last_error || 'error'}</span>}
                {!status.last_status && '—'}
              </td>
            </tr>
            {status.last_status === 'ok' && (
              <>
                <tr><td><strong>Last size</strong></td><td>{fmtBytes(status.last_size_bytes)}</td></tr>
                <tr><td><strong>Last rows</strong></td><td>{status.last_rows.toLocaleString()}</td></tr>
                <tr><td><strong>Last duration</strong></td><td>{(status.last_duration_ms / 1000).toFixed(1)}s</td></tr>
              </>
            )}
          </tbody>
        </table>
        <button onClick={runNow} disabled={busy} style={styles.btn}>
          {busy ? 'Running...' : 'Run backup now'}
        </button>
        <button onClick={load} style={{ ...styles.btnSecondary, marginLeft: 8 }}>Refresh</button>
      </div>

      <DisaSyncCard />
      <IsnSyncCard />
      <RmInvoiceSyncCard />

      <HascSyncCard />

      <div style={styles.card}>
        <h2 style={styles.h2}>Recent backups in R2</h2>
        {!list.length && (
          <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
            {status.configured
              ? 'No backups uploaded yet. Click "Run backup now" to create the first one.'
              : 'R2 not configured. Set BACKUP_R2_ACCOUNT_ID / BACKUP_R2_ACCESS_KEY_ID / BACKUP_R2_SECRET_ACCESS_KEY / BACKUP_R2_BUCKET on Railway.'}
          </p>
        )}
        {list.length > 0 && (
          <table style={styles.table}>
            <thead>
              <tr><th>Uploaded</th><th>Key</th><th>Size</th></tr>
            </thead>
            <tbody>
              {list.map(item => (
                <tr key={item.key}>
                  <td>{item.uploaded_at ? fmtDateTime(item.uploaded_at) : '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{item.key}</td>
                  <td>{fmtBytes(item.size_bytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={styles.card}>
        <h2 style={styles.h2}>How to restore</h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          From a shell with <code>DATABASE_URL</code> pointing at the target (recovery)
          database and the same <code>BACKUP_R2_*</code> env vars, run:
        </p>
        <pre style={{ background: '#F3F4F6', padding: 12, borderRadius: 4, fontSize: 12, overflowX: 'auto' }}>
{`# 1. Boot the server once so initDB() creates empty tables, then stop it.
# 2. Restore from R2 by key:
node server/scripts/restore.js --key daily/sss-crm-XXXX.json.gz --i-know-this-truncates

# Or from a local file:
node server/scripts/restore.js --file ./sss-crm-XXXX.json.gz --i-know-this-truncates`}
        </pre>
        <p style={{ color: '#6B7280', fontSize: 12 }}>
          The restore script TRUNCATEs all dumped tables before re-inserting, so only
          point it at a recovery DB (not production) unless you mean to overwrite.
          Project attachment <em>files</em> on the Railway volume are NOT in this backup
          — only the <code>attachments</code> table rows. Snapshot the volume separately.
        </p>
      </div>
    </>
  );
}

// ---- Settings tab ------------------------------------------------------

// RM Quote Notifications (George 2026-11-04): who hears when sales ISSUES a
// quote on the RM Quotes tab. Branch quotes go to the branch's own manager,
// set per branch on Admin > Branches.
function RmQuoteNotifyCard() {
  const KEYS = [
    ['rm_quote_notify_emails', 'Every issued quote', 'ali.knox@selectsafetyusa.com'],
    ['rm_quote_notify_rescue_emails', 'Added for 900 Rescue quotes', 'kevin.leach@selectsafetyusa.com'],
    ['rm_quote_notify_ta_emails', 'Added for 800 Turnaround quotes', 'george.smith@selectsafetyusa.com'],
  ];
  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    api.getSettings().then(s => setDraft(Object.fromEntries(KEYS.map(([k]) => [k, s[k] || ''])))).catch(e => setMsg({ ok: false, text: e.message }));
  }, []);
  const save = async () => {
    setMsg(null);
    const clean = (v) => String(v || '').split(',').map(x => x.trim()).filter(Boolean);
    const bad = KEYS.flatMap(([k]) => clean(draft[k])).filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setMsg({ ok: false, text: `These don't look like email addresses: ${bad.join(', ')}` }); return; }
    try {
      for (const [k] of KEYS) await api.updateSetting(k, clean(draft[k]).join(','));
      setMsg({ ok: true, text: '✓ Saved' });
    } catch (e) { setMsg({ ok: false, text: e.message }); }
  };
  if (!draft) return null;
  return (
    <div style={styles.card}>
      <h2 style={styles.h2}>RM Quote Notifications</h2>
      <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
        Sent when sales <strong>issues</strong> a quote on the RM Quotes tab, with the customer PDF attached.
        Branch (100-700) quotes also go to that branch's manager, set on <strong>Admin &gt; Branches</strong>.
        The rep who issues it is CC'd. Comma-separated.
      </p>
      {KEYS.map(([k, label, ph]) => (
        <div key={k} style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>{label}</label>
          <input style={{ ...styles.input, width: '100%', maxWidth: 560 }} placeholder={ph}
            value={draft[k]} onChange={e => setDraft({ ...draft, [k]: e.target.value })} />
        </div>
      ))}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button style={styles.btn} onClick={save}>Save</button>
        {msg && <span style={{ color: msg.ok ? '#10B981' : '#B91C1C', fontSize: 13 }}>{msg.text}</span>}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [staleDraft, setStaleDraft] = useState('');
  const [notifyDraft, setNotifyDraft] = useState('');
  const [rescueDraft, setRescueDraft] = useState('');
  const [estimateNotifyDraft, setEstimateNotifyDraft] = useState('');
  const [wonDraft, setWonDraft] = useState('');
  const [standbyDraft, setStandbyDraft] = useState('');
  const [standbySaved, setStandbySaved] = useState(false);
  const [digestExcludeDraft, setDigestExcludeDraft] = useState('');
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [notifySaved, setNotifySaved] = useState(false);
  const [estimateNotifySaved, setEstimateNotifySaved] = useState(false);
  const [wonSaved, setWonSaved] = useState(false);
  const [digestExcludeSaved, setDigestExcludeSaved] = useState(false);
  const [markupDraft, setMarkupDraft] = useState('');
  const [markupSaved, setMarkupSaved] = useState(false);

  useEffect(() => {
    api.getSettings().then(s => {
      setSettings(s);
      setStaleDraft(s.stale_account_days || '60');
      setNotifyDraft(s.request_notify_emails || '');
      setRescueDraft(s.request_notify_rescue_emails || '');
      setEstimateNotifyDraft(s.estimate_notify_emails || '');
      setWonDraft(s.won_alert_emails || '');
      setStandbyDraft(s.mobilization_standby_emails || '');
      setDigestExcludeDraft(s.weekly_submission_digest_exclude || '');
      setMarkupDraft(s.consumable_default_markup != null
        ? String(Math.round(Number(s.consumable_default_markup) * 1000) / 10) : '15');
    }).catch(e => setError(e.message));
  }, []);

  const saveNotify = async () => {
    setError(''); setNotifySaved(false);
    const clean = (v) => v.split(',').map(x => x.trim()).filter(Boolean).join(',');
    const bad = [...notifyDraft.split(','), ...rescueDraft.split(',')]
      .map(x => x.trim()).filter(Boolean).filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setError(`These don't look like email addresses: ${bad.join(', ')}`); return; }
    try {
      await api.updateSetting('request_notify_emails', clean(notifyDraft));
      await api.updateSetting('request_notify_rescue_emails', clean(rescueDraft));
      setSettings({ ...settings, request_notify_emails: clean(notifyDraft), request_notify_rescue_emails: clean(rescueDraft) });
      setNotifySaved(true);
      setTimeout(() => setNotifySaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  const saveEstimateNotify = async () => {
    setError(''); setEstimateNotifySaved(false);
    const clean = (v) => v.split(',').map(x => x.trim()).filter(Boolean).join(',');
    const bad = estimateNotifyDraft.split(',').map(x => x.trim()).filter(Boolean)
      .filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setError(`These don't look like email addresses: ${bad.join(', ')}`); return; }
    try {
      await api.updateSetting('estimate_notify_emails', clean(estimateNotifyDraft));
      setSettings({ ...settings, estimate_notify_emails: clean(estimateNotifyDraft) });
      setEstimateNotifySaved(true);
      setTimeout(() => setEstimateNotifySaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  const saveWonAlert = async () => {
    setError(''); setWonSaved(false);
    const clean = (v) => v.split(',').map(x => x.trim()).filter(Boolean).join(',');
    const bad = wonDraft.split(',').map(x => x.trim()).filter(Boolean)
      .filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setError(`These don't look like email addresses: ${bad.join(', ')}`); return; }
    try {
      await api.updateSetting('won_alert_emails', clean(wonDraft));
      setSettings({ ...settings, won_alert_emails: clean(wonDraft) });
      setWonSaved(true);
      setTimeout(() => setWonSaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  const saveStandbyAlert = async () => {
    setError(''); setStandbySaved(false);
    const clean = (v) => v.split(',').map(x => x.trim()).filter(Boolean).join(',');
    const bad = standbyDraft.split(',').map(x => x.trim()).filter(Boolean)
      .filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setError(`These don't look like email addresses: ${bad.join(', ')}`); return; }
    try {
      await api.updateSetting('mobilization_standby_emails', clean(standbyDraft));
      setSettings({ ...settings, mobilization_standby_emails: clean(standbyDraft) });
      setStandbySaved(true);
      setTimeout(() => setStandbySaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  // Suppression list, not a recipient list — everything else on this tab adds
  // people, this one removes them. Same validation so a typo can't quietly
  // suppress nobody.
  const saveDigestExclude = async () => {
    setError(''); setDigestExcludeSaved(false);
    const clean = (v) => v.split(',').map(x => x.trim()).filter(Boolean).join(',');
    const bad = digestExcludeDraft.split(',').map(x => x.trim()).filter(Boolean)
      .filter(x => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x));
    if (bad.length) { setError(`These don't look like email addresses: ${bad.join(', ')}`); return; }
    try {
      await api.updateSetting('weekly_submission_digest_exclude', clean(digestExcludeDraft));
      setSettings({ ...settings, weekly_submission_digest_exclude: clean(digestExcludeDraft) });
      setDigestExcludeSaved(true);
      setTimeout(() => setDigestExcludeSaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  const saveStale = async () => {
    setError(''); setSaved(false);
    const n = Number(staleDraft);
    if (!Number.isFinite(n) || n < 7 || n > 365) {
      setError('Stale window must be a number between 7 and 365 days.');
      return;
    }
    try {
      await api.updateSetting('stale_account_days', String(n));
      setSettings({ ...settings, stale_account_days: String(n) });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  const toggleEstimating = async () => {
    setError('');
    const next = settings.estimating_enabled === 'true' ? 'false' : 'true';
    try {
      await api.updateSetting('estimating_enabled', next);
      setSettings({ ...settings, estimating_enabled: next });
    } catch (err) { setError(err.message); }
  };

  const saveMarkup = async () => {
    setError(''); setMarkupSaved(false);
    const pct = Number(markupDraft);
    if (!Number.isFinite(pct) || pct < 0 || pct >= 1000) {
      setError('Consumable markup must be a percent between 0 and 1000.');
      return;
    }
    const frac = Math.round((pct / 100) * 10000) / 10000;
    try {
      await api.updateSetting('consumable_default_markup', String(frac));
      setSettings({ ...settings, consumable_default_markup: String(frac) });
      setMarkupSaved(true);
      setTimeout(() => setMarkupSaved(false), 2000);
    } catch (err) { setError(err.message); }
  };

  if (!settings) return <div>Loading...</div>;

  const estimatingOn = settings.estimating_enabled === 'true';

  return (
    <>
      <h2 style={styles.h2}>System Settings</h2>
      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.card}>
        <h2 style={styles.h2}>Stale Account Window</h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          An account is flagged 🌵 STALE on the Accounts page when there's been no
          call log entry, project edit, or recent project work in this many days.
          Default: 60. Range: 7-365.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <input
            type="number" min={7} max={365}
            style={{ ...styles.input, width: 120 }}
            value={staleDraft}
            onChange={e => setStaleDraft(e.target.value)}
          />
          <span style={{ color: '#6B7280', fontSize: 13 }}>days</span>
          <button style={styles.btn} onClick={saveStale}>Save</button>
          {saved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.h2}>Estimating Module</h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          Turn the Estimating module on or off for admin and leadership.
          When off, the nav entry is hidden and all <code>/api/estimates</code> endpoints
          return 403 regardless of role.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            onClick={toggleEstimating}
            style={{
              padding: '6px 20px', borderRadius: 6, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', border: '1px solid',
              background: estimatingOn ? '#D1FAE5' : '#F3F4F6',
              color: estimatingOn ? '#065F46' : '#374151',
              borderColor: estimatingOn ? '#10B981' : '#D1D5DB',
            }}
          >
            {estimatingOn ? '✓ Enabled' : 'Disabled'}
          </button>
          <span style={{ color: '#6B7280', fontSize: 13 }}>
            {estimatingOn ? 'Estimating is visible to admin and leadership.' : 'Estimating is hidden from all users.'}
          </span>
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.h2}>Consumable Default Markup</h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          Default markup-over-cost applied to consumable lines on new estimates.
          Bill Rate = Cost × (1 + markup). Each estimate seeds from this value and
          can be overridden on its Consumables tab (per estimate and per line).
          Changing it here only affects estimates created afterward. Default: 15%.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <input
            type="number" min={0} max={1000} step={0.1}
            style={{ ...styles.input, width: 120 }}
            value={markupDraft}
            onChange={e => setMarkupDraft(e.target.value)}
          />
          <span style={{ color: '#6B7280', fontSize: 13 }}>% markup</span>
          <button style={styles.btn} onClick={saveMarkup}>Save</button>
          {markupSaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.h2}>Request Notifications</h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          Everyone listed goes on the <strong>same email</strong> (one To line — reply-all works) for
          request submissions, revisions, and new-file uploads (plus the submitter's supervisor and the AM).
          This is the back-and-forth chain — for the estimate ready-for-approval /
          approved-or-rejected emails, use Estimate Approval Notifications below. Comma-separated.
        </p>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
            Always notified
          </label>
          <input style={{ ...styles.input, width: '100%', maxWidth: 560 }}
            placeholder="george.smith@selectsafetyusa.com, ali.knox@..."
            value={notifyDraft}
            onChange={e => setNotifyDraft(e.target.value)} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
            Added when the request scope includes Rescue or CSVM
</label>
          <input style={{ ...styles.input, width: '100%', maxWidth: 560 }}
            placeholder="Kevin.Leach@selectsafetyusa.com"
            value={rescueDraft}
            onChange={e => setRescueDraft(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button style={styles.btn} onClick={saveNotify}>Save</button>
          {notifySaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
      </div>

      <RmQuoteNotifyCard />

      <div style={styles.card}>
        <h2 style={styles.h2}>Estimate Approval Notifications</h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          Separate from Request Notifications above — these people get ONLY the
          estimate ready-for-approval email and the Approved/Rejected decision email,
          not request submissions or revisions. Comma-separated.
        </p>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
            Notified on estimate ready-for-approval + decision
          </label>
          <input style={{ ...styles.input, width: '100%', maxWidth: 560 }}
            placeholder="george.smith@selectsafetyusa.com, matt...@..."
            value={estimateNotifyDraft}
            onChange={e => setEstimateNotifyDraft(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button style={styles.btn} onClick={saveEstimateNotify}>Save</button>
          {estimateNotifySaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.h2}>Project WON Alert</h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          These people get the 🏆 Project Won email when an opportunity moves to
          Won, alongside the opportunity's own account manager. It is
          <strong> not</strong> role-driven — nobody is added for being admin,
          leadership, or ops. Comma-separated.
        </p>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
            Notified on every Won
          </label>
          <input style={{ ...styles.input, width: '100%', maxWidth: 560 }}
            placeholder="matt.smith@selectsafetyusa.com, george.smith@..."
            value={wonDraft}
            onChange={e => setWonDraft(e.target.value)} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 12, marginTop: 0 }}>
          Rescue jobs (branch 900-999, or a request scoped Rescue) also get the
          rescue list from <strong>Request Notifications</strong> above — that's
          how Kevin Leach lands on rescue Won alerts only.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button style={styles.btn} onClick={saveWonAlert}>Save</button>
          {wonSaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.h2}>Mobilization Standby Alert</h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          Leadership gets the ⏱ Mobilization Standby email when an opportunity
          moves to <strong>Mobilization Standby</strong>: not awarded yet, but the
          award could come at the last minute, so we must be operationally ready
          to mobilize. Seeded from the leadership accounts; edit freely. The
          person who changed the status is CC'd. Comma-separated.
        </p>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
            Notified on every move to Mobilization Standby
          </label>
          <input style={{ ...styles.input, width: '100%', maxWidth: 560 }}
            placeholder="leadership@selectsafetyusa.com, ..."
            value={standbyDraft}
            onChange={e => setStandbyDraft(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button style={styles.btn} onClick={saveStandbyAlert}>Save</button>
          {standbySaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.h2}>Weekly Call-Log Digest — Opt Out</h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          The Monday <strong>"N reps missing weekly call log"</strong> email goes
          to every supervisor (their own reports) and to every admin/leadership
          account (all reps). Anyone listed here is dropped from it. This is the
          only field on this tab that <strong>removes</strong> people rather than
          adding them. Comma-separated.
        </p>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
            Do not send the digest to
          </label>
          <input style={{ ...styles.input, width: '100%', maxWidth: 560 }}
            placeholder="george.smith@selectsafetyusa.com"
            value={digestExcludeDraft}
            onChange={e => setDigestExcludeDraft(e.target.value)} />
        </div>
        <p style={{ color: '#6B7280', fontSize: 12, marginTop: 0 }}>
          Excluding someone only stops <em>their</em> copy — every other
          supervisor and leadership account still gets the same list, so no rep
          goes unwatched. The per-rep nudge to the reps themselves is separate
          and unaffected.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button style={styles.btn} onClick={saveDigestExclude}>Save</button>
          {digestExcludeSaved && <span style={{ color: '#10B981', fontSize: 13 }}>✓ Saved</span>}
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.h2}>Reminder Toggles</h2>
        <p style={{ color: '#6B7280', fontSize: 13, marginTop: 0 }}>
          Live on the <strong>Mail</strong> tab. Daily follow-up reminders + weekly
          submission reminders can be turned off / on there.
        </p>
      </div>
    </>
  );
}
